// VideoPostEditor.js — Professional Video Content Studio
// Sections: Today → Story → Film → Studio
// Auto-save · AI Voice · Inline overlays · Guided pathway

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import DesignStudio from '../../DesignStudio';
import { businessAPI } from '../../../../../services/businessAPI';

// ─── Config ──────────────────────────────────────────────────────────────────

const SECTION_IDS = ['today', 'script', 'film', 'edit'];

const PLATFORM_CFG = {
  instagram: { name: 'Instagram Reel', icon: 'fab fa-instagram', color: '#E4405F', maxDuration: 90 },
  tiktok:    { name: 'TikTok',          icon: 'fab fa-tiktok',    color: '#000000', maxDuration: 180 },
  youtube:   { name: 'YouTube Short',   icon: 'fab fa-youtube',   color: '#FF0000', maxDuration: 60 },
  facebook:  { name: 'Facebook Reel',   icon: 'fab fa-facebook',  color: '#1877F2', maxDuration: 90 },
  linkedin:  { name: 'LinkedIn Video',  icon: 'fab fa-linkedin',  color: '#0A66C2', maxDuration: 600 },
  twitter:   { name: 'Twitter/X Video', icon: 'fab fa-twitter',   color: '#1DA1F2', maxDuration: 140 },
};

const ASPECT_RATIOS = [
  { id: '9:16', w: 9,  h: 16, label: 'Vertical',  hint: 'Reels · Shorts · TikTok' },
  { id: '1:1',  w: 1,  h: 1,  label: 'Square',    hint: 'Feed · Stories' },
  { id: '16:9', w: 16, h: 9,  label: 'Landscape', hint: 'YouTube · LinkedIn' },
];

const FILTER_PRESETS = [
  { id: 'natural',   label: 'Natural',   css: 'none' },
  { id: 'vivid',     label: 'Vivid',     css: 'saturate(1.4) contrast(1.1)' },
  { id: 'warm',      label: 'Warm',      css: 'sepia(0.3) saturate(1.2) brightness(1.05)' },
  { id: 'cool',      label: 'Cool',      css: 'hue-rotate(30deg) saturate(0.85) brightness(0.98)' },
  { id: 'dramatic',  label: 'Dramatic',  css: 'contrast(1.4) saturate(0.8) brightness(0.9)' },
  { id: 'vintage',   label: 'Vintage',   css: 'sepia(0.5) contrast(0.9) brightness(0.95)' },
  { id: 'cinematic', label: 'Cinematic', css: 'contrast(1.2) saturate(0.85) brightness(0.9) hue-rotate(-5deg)' },
  { id: 'bright',    label: 'Bright',    css: 'brightness(1.18) saturate(1.1) contrast(0.95)' },
];

const TRANSITIONS = [
  { id: 'cut',   label: 'Cut',      icon: 'fas fa-scissors' },
  { id: 'fade',  label: 'Fade',     icon: 'fas fa-ghost' },
  { id: 'zoom',  label: 'Zoom In',  icon: 'fas fa-search-plus' },
  { id: 'slide', label: 'Slide',    icon: 'fas fa-arrow-right' },
  { id: 'whip',  label: 'Whip Pan', icon: 'fas fa-wind' },
];

const MUSIC_MOODS = [
  { id: 'none',         label: 'No Music',      icon: 'fas fa-volume-mute',  color: '#94A3B8', desc: 'Voice only' },
  { id: 'energetic',    label: 'Energetic',     icon: 'fas fa-bolt',         color: '#F59E0B', desc: '132–140 BPM · driving' },
  { id: 'upbeat',       label: 'Upbeat',        icon: 'fas fa-music',        color: '#10B981', desc: '115–125 BPM · positive' },
  { id: 'calm',         label: 'Calm',          icon: 'fas fa-leaf',         color: '#6366F1', desc: '65–80 BPM · ambient' },
  { id: 'emotional',    label: 'Emotional',     icon: 'fas fa-heart',        color: '#EC4899', desc: '78–92 BPM · heartfelt' },
  { id: 'corporate',    label: 'Corporate',     icon: 'fas fa-briefcase',    color: '#0EA5E9', desc: '95–108 BPM · confident' },
  { id: 'trendy',       label: 'Trending',      icon: 'fas fa-star',         color: '#8B5CF6', desc: '124–135 BPM · modern' },
  { id: 'lofi',         label: 'Lo-Fi',         icon: 'fas fa-headphones',   color: '#84CC16', desc: '80–90 BPM · chill study' },
  { id: 'cinematic',    label: 'Cinematic',     icon: 'fas fa-film',         color: '#B45309', desc: '55–72 BPM · epic' },
  { id: 'motivational', label: 'Motivational',  icon: 'fas fa-fire',         color: '#DC2626', desc: '118–130 BPM · powerful' },
  { id: 'jazz',         label: 'Jazz',          icon: 'fas fa-drum',         color: '#92400E', desc: '105–120 BPM · swing' },
  { id: 'romantic',     label: 'Romantic',      icon: 'fas fa-moon',         color: '#BE185D', desc: '68–80 BPM · tender' },
  { id: 'pop',          label: 'Pop',           icon: 'fas fa-compact-disc', color: '#0891B2', desc: '118–128 BPM · catchy' },
  { id: 'tech',         label: 'Tech',          icon: 'fas fa-microchip',    color: '#1D4ED8', desc: '124–138 BPM · digital' },
];

const TEXT_ANIM = [
  { id: 'scale',      label: 'Pop',        cssClass: 'ov-anim-scale'      },
  { id: 'slideUp',    label: 'Slide Up',   cssClass: 'ov-anim-slideUp'    },
  { id: 'slideLeft',  label: 'Slide Left', cssClass: 'ov-anim-slideLeft'  },
  { id: 'bounce',     label: 'Bounce',     cssClass: 'ov-anim-bounce'     },
  { id: 'fade',       label: 'Fade',       cssClass: 'ov-anim-fade'       },
  { id: 'typewriter', label: 'Typewriter', cssClass: 'ov-anim-typewriter' },
  { id: 'zoomIn',    label: 'Zoom In',    cssClass: 'ov-anim-zoomIn'     },
  { id: 'flipDown',  label: 'Flip',       cssClass: 'ov-anim-flipDown'   },
  { id: 'reveal',    label: 'Reveal',     cssClass: 'ov-anim-reveal'     },
  { id: 'glitch',    label: 'Glitch',     cssClass: 'ov-anim-glitch'     },
  { id: 'none',       label: 'None',       cssClass: ''                   },
];
const ANIM_CLASS = Object.fromEntries(TEXT_ANIM.map(a => [a.id, a.cssClass]));

const CAPTION_STYLES = [
  { id: 'word-by-word', label: 'Word by Word', desc: 'TikTok / Reels style — each word pops' },
  { id: 'sentence',     label: 'Full Sentence', desc: 'Classic subtitle style' },
  { id: 'none',         label: 'No Captions',   desc: 'Clean — voice only' },
];

// 20 royalty-free Google Fonts
const FONT_OPTIONS = [
  { label: 'Poppins',          value: 'Poppins',          category: 'sans' },
  { label: 'Montserrat',       value: 'Montserrat',       category: 'sans' },
  { label: 'Inter',            value: 'Inter',            category: 'sans' },
  { label: 'Raleway',          value: 'Raleway',          category: 'sans' },
  { label: 'Nunito',           value: 'Nunito',           category: 'sans' },
  { label: 'Oswald',           value: 'Oswald',           category: 'condensed' },
  { label: 'Bebas Neue',       value: 'Bebas Neue',       category: 'condensed' },
  { label: 'Josefin Sans',     value: 'Josefin Sans',     category: 'sans' },
  { label: 'Space Grotesk',    value: 'Space Grotesk',    category: 'sans' },
  { label: 'DM Sans',          value: 'DM Sans',          category: 'sans' },
  { label: 'Outfit',           value: 'Outfit',           category: 'sans' },
  { label: 'Lato',             value: 'Lato',             category: 'sans' },
  { label: 'Roboto',           value: 'Roboto',           category: 'sans' },
  { label: 'Ubuntu',           value: 'Ubuntu',           category: 'sans' },
  { label: 'Playfair Display', value: 'Playfair Display', category: 'serif' },
  { label: 'Abril Fatface',    value: 'Abril Fatface',    category: 'serif' },
  { label: 'Titan One',        value: 'Titan One',        category: 'display' },
  { label: 'Dancing Script',   value: 'Dancing Script',   category: 'handwriting' },
  { label: 'Pacifico',         value: 'Pacifico',         category: 'handwriting' },
  { label: 'Permanent Marker', value: 'Permanent Marker', category: 'handwriting' },
];
const FONT_SIZES = [20, 24, 28, 32, 40, 48, 56, 64, 72, 80];

const BRIEFING_PHASES = [
  { icon: 'fa-video',        text: 'Analysing your video content strategy…' },
  { icon: 'fa-microphone',   text: 'Crafting platform-specific brief…' },
  { icon: 'fa-lightbulb',    text: 'Generating creative direction…' },
  { icon: 'fa-chart-line',   text: 'Finalising performance goals…' },
];

const AUTO_EDIT_STEPS = [
  { icon: 'fa-brain',             text: 'Analysing your content & platform…' },
  { icon: 'fa-film',              text: 'Planning text overlays & timing…' },
  { icon: 'fa-palette',           text: 'Selecting colour grade & filter…' },
  { icon: 'fa-music',             text: 'Choosing music mood & audio style…' },
  { icon: 'fa-closed-captioning', text: 'Configuring captions & effects…' },
  { icon: 'fa-wand-magic-sparkles', text: 'Applying the magic…' },
];

// OpenAI TTS voices — mapped to style + gender
// Female: shimmer (warm British-feel), fable (British accent), nova (natural US)
// Male:   onyx (deep British-feel), echo (warm), alloy (neutral)
const TTS_VOICES = {
  female: [
    { id: 'shimmer', label: 'Warm & Friendly', desc: 'Soft · British-feel · approachable' },
    { id: 'fable',   label: 'British Female',  desc: 'Clear · British accent · polished' },
    { id: 'nova',    label: 'Natural',          desc: 'Natural · energetic · clear' },
  ],
  male: [
    { id: 'onyx',  label: 'Deep & Authoritative', desc: 'Deep · British-feel · confident' },
    { id: 'echo',  label: 'Warm Male',             desc: 'Warm · conversational · friendly' },
    { id: 'alloy', label: 'Neutral Male',          desc: 'Neutral · clean · professional' },
  ],
};

// Web Speech API fallback styles (used if API call fails)
const VOICE_STYLES = [
  { id: 'warm',         label: 'Warm & Friendly',   desc: 'Natural · approachable', rate: 0.90, pitch: 1.12 },
  { id: 'professional', label: 'Professional',       desc: 'Confident · polished',   rate: 0.85, pitch: 1.05 },
  { id: 'energetic',    label: 'Upbeat',             desc: 'Enthusiastic · lively',  rate: 1.02, pitch: 1.18 },
  { id: 'calm',         label: 'Calm & Soothing',    desc: 'Gentle · reassuring',    rate: 0.78, pitch: 1.08 },
];

// Female voice names — British English voices prioritised
const PREFERRED_FEMALE_VOICES = [
  // British English (highest priority)
  'Serena', 'Kate', 'Microsoft Hazel', 'Microsoft Susan',
  'Google UK English Female', 'en-GB-Neural2-A', 'en-GB-Neural2-C',
  // macOS / iOS
  'Samantha', 'Siri', 'Karen', 'Moira', 'Tessa', 'Veena', 'Victoria',
  // Windows
  'Microsoft Zira', 'Microsoft Eva', 'Microsoft Jenny', 'Microsoft Aria',
  // Chrome Neural
  'Google US English Female',
  'en-US-Neural2-C', 'en-US-Neural2-E', 'en-US-Neural2-F', 'en-US-Neural2-H',
];

const getBestFemaleVoice = () => {
  if (!('speechSynthesis' in window)) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  for (const name of PREFERRED_FEMALE_VOICES) {
    const v = voices.find(v => v.name.includes(name));
    if (v) return v;
  }
  // Any en-GB voice
  const enGb = voices.find(v => (v.lang || '').startsWith('en-GB') || (v.lang || '').startsWith('en_GB'));
  if (enGb) return enGb;
  // Any voice with female markers in name
  const female = voices.find(v =>
    /female|woman|girl|zira|samantha|karen|moira|tessa|victoria|jenny|aria|eva|hazel|serena|kate/i.test(v.name)
  );
  if (female) return female;
  const enUs = voices.filter(v => (v.lang || '').startsWith('en-US') || (v.lang || '').startsWith('en_US'));
  return enUs[1] || enUs[0] || voices[0];
};

const SCENE_TYPE_COLORS = {
  hook:     { bg: '#FEF3C7', text: '#92400E', label: 'HOOK' },
  problem:  { bg: '#FEE2E2', text: '#991B1B', label: 'PROBLEM' },
  solution: { bg: '#D1FAE5', text: '#065F46', label: 'SOLUTION' },
  proof:    { bg: '#DBEAFE', text: '#1E40AF', label: 'PROOF' },
  cta:      { bg: '#EDE9FE', text: '#5B21B6', label: 'CTA' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtTime = (s) => {
  if (!s && s !== 0) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SH({ children }) {
  return <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">{children}</p>;
}

function Toggle({ on, onToggle, label, sub }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <span className="text-[11px] font-medium text-slate-700 block">{label}</span>
        {sub && <span className="text-[9px] text-slate-400">{sub}</span>}
      </div>
      <button onClick={onToggle}
        className={`w-9 h-5 rounded-full transition-all flex-shrink-0 ${on ? 'bg-slate-800' : 'bg-slate-300'}`}>
        <div className="w-4 h-4 rounded-full bg-white shadow transition-all"
          style={{ marginLeft: on ? '18px' : '2px' }} />
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function VideoPostEditor({ day, businessData, strategyData, onSave, onClose }) {
  // ── Section navigation ────────────────────────────────────────────────────
  const [activeSection, setActiveSection] = useState('today');
  const sectionIdx = SECTION_IDS.indexOf(activeSection);

  // ── Briefing ──────────────────────────────────────────────────────────────
  const [briefingData,    setBriefingData]    = useState(day?.brief || null);
  const [isLoadingBrief,  setIsLoadingBrief]  = useState(false);
  const [briefPhase,      setBriefPhase]      = useState(0);

  // ── Script / Content ──────────────────────────────────────────────────────
  const [postTitle,        setPostTitle]        = useState(day?.postTitle || day?.task || '');
  const [tone,             setTone]             = useState(day?.tone || 'professional');
  const [targetDuration,   setTargetDuration]   = useState(30);
  const [scriptData,       setScriptData]       = useState(day?.videoData?.scriptData || null);
  const [selectedHookIdx,  setSelectedHookIdx]  = useState(0);
  const [hookOptions,      setHookOptions]      = useState([]);
  const [caption,          setCaption]          = useState(day?.caption || '');
  const [hashtags,         setHashtags]         = useState(day?.hashtags || []);
  const [ctaText,          setCtaText]          = useState(day?.videoData?.scriptData?.cta || 'Book Now');
  const [expandedScene,    setExpandedScene]    = useState(null);
  const [isGenScript,      setIsGenScript]      = useState(false);
  const [isGenHooks,       setIsGenHooks]       = useState(false);

  // ── Clips ────────────────────────────────────────────────────────────────
  const [clips,            setClips]            = useState(day?.videoData?.clips || []);
  const [selectedClipId,   setSelectedClipId]   = useState(null);

  // ── Video playback ────────────────────────────────────────────────────────
  const videoRef       = useRef(null);
  const canvasRef      = useRef(null);
  const animRef        = useRef(null);
  const playPendingRef = useRef(false); // guard against concurrent play() calls
  const [isPlaying,        setIsPlaying]        = useState(false);
  const [currentTime,      setCurrentTime]      = useState(0);
  const [totalDuration,    setTotalDuration]    = useState(0);
  const [aspectRatio,      setAspectRatio]      = useState(day?.videoData?.aspectRatio || '9:16');

  // ── Text overlays ─────────────────────────────────────────────────────────
  const [textOverlays, setTextOverlays] = useState(
    day?.videoData?.textOverlays || []
  );
  const [editingOverlayId, setEditingOverlayId] = useState(null);
  const [globalTextStyle, setGlobalTextStyle] = useState(
    day?.videoData?.globalTextStyle || { fontFamily: 'Poppins', fontSize: 64, bold: true }
  );

  // ── Audio ─────────────────────────────────────────────────────────────────
  const [audioConfig, setAudioConfig] = useState(
    day?.videoData?.audioConfig || { mood: 'none', volume: 40, autoDuck: true, fadeIn: false, fadeOut: false, videoVolume: 100 }
  );
  const [customMusicFile, setCustomMusicFile] = useState(null);
  const audioRef = useRef(null);

  // ── Effects ───────────────────────────────────────────────────────────────
  const [effectsConfig, setEffectsConfig] = useState(
    day?.videoData?.effectsConfig || { filter: 'natural', brightness: 0, contrast: 0, saturation: 0, transition: 'cut', speed: 1, stabilise: false }
  );

  // ── Captions ─────────────────────────────────────────────────────────────
  const [captionConfig, setCaptionConfig] = useState(
    day?.videoData?.captionConfig || { style: 'word-by-word', position: 'bottom', background: 'box', size: 24, color: '#ffffff' }
  );

  // ── Thumbnail ─────────────────────────────────────────────────────────────
  const [thumbnailMode,    setThumbnailMode]    = useState('none'); // 'none' | 'frame' | 'studio'
  const [thumbnailUrl,     setThumbnailUrl]     = useState(day?.imageUrl || '');
  const [thumbnailDesign,  setThumbnailDesign]  = useState(day?.designData || null);
  const thumbCanvasRef = useRef(null);

  // ── Cover image (shown over video for first 3 s) ───────────────────────
  const [coverImageUrl,   setCoverImageUrl]   = useState(day?.videoData?.coverImageUrl || '');
  const coverImgRef = useRef(null); // preloaded Image object for canvas drawing

  // ── Editor tab ────────────────────────────────────────────────────────────
  const [controlTab, setControlTab] = useState('text');

  // ── Phone preview popup ───────────────────────────────────────────────────
  const [showPreview,      setShowPreview]      = useState(false);
  const [previewBlobUrl,   setPreviewBlobUrl]   = useState(''); // captured at open time
  const [previewPlaying,   setPreviewPlaying]   = useState(false);
  const [previewCurTime,   setPreviewCurTime]   = useState(0);
  const previewVideoRef  = useRef(null);
  const previewCanvasRef = useRef(null);
  const previewAnimRef   = useRef(null);

  // ── Panel layout ──────────────────────────────────────────────────────────
  const [panelOpen,        setPanelOpen]        = useState(true);
  const [autoEditCollapsed,setAutoEditCollapsed]= useState(false);

  // ── Music preview state (standalone — not tied to main video playback) ────
  const [musicPreviewPlaying, setMusicPreviewPlaying] = useState(false);

  // ── AI Auto-Edit ──────────────────────────────────────────────────────────
  const [isAutoEditing,    setIsAutoEditing]    = useState(false);
  const [autoEditStep,     setAutoEditStep]     = useState(0);
  const [autoEditDone,     setAutoEditDone]     = useState(false);
  const [autoEditSummary,  setAutoEditSummary]  = useState('');
  const [autoEditError,    setAutoEditError]    = useState('');

  // ── Guided pathway state ───────────────────────────────────────────────────
  const [scriptApproved,   setScriptApproved]   = useState(day?.videoData?.scriptApproved || false);
  const [cheatSheetOpen,   setCheatSheetOpen]   = useState(false);
  const [isExporting,      setIsExporting]      = useState(false);
  const editTriggeredRef   = useRef(false);
  const exportedBlobRef    = useRef(null); // stores the last exported blob for publishing
  const exportMusicRef     = useRef(null); // tracks moodExportEl so unmount can silence it

  // ── Publish states ────────────────────────────────────────────────────────
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [isPublishing,       setIsPublishing]       = useState(false);
  const [publishError,       setPublishError]       = useState('');
  const [publishSuccess,     setPublishSuccess]     = useState(false);

  // ── Voice synthesis state ──────────────────────────────────────────────────
  const [voiceStyle,       setVoiceStyle]       = useState('warm');
  const [voiceGender,      setVoiceGender]      = useState('female');          // 'female' | 'male'
  const [ttsVoice,         setTtsVoice]         = useState('shimmer');         // OpenAI TTS voice id
  const [isVoicePlaying,   setIsVoicePlaying]   = useState(false);
  const [ttsStatus,        setTtsStatus]        = useState('idle');            // 'idle'|'loading'|'playing'|'error'
  const [ttsError,         setTtsError]         = useState('');
  const ttsAudioRef    = useRef(null);  // HTMLAudioElement for OpenAI TTS playback
  const ttsBlobUrlRef  = useRef(null);  // Persistent TTS blob URL — kept alive for export mixer

  // ── Output mode: voice+music or music-only ────────────────────────────────
  const [outputMode,       setOutputMode]       = useState(day?.videoData?.outputMode || 'music-only');

  // ── Auto-save ─────────────────────────────────────────────────────────────
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const autoSaveTimerRef = useRef(null);

  // ── Always-current refs (stale closure prevention) ────────────────────────
  const onSaveRef      = useRef(onSave);
  const dayRef         = useRef(day);
  const postTitleRef   = useRef(postTitle);
  const toneRef        = useRef(tone);
  const captionRef     = useRef(caption);
  const hashtagsRef    = useRef(hashtags);
  const scriptDataRef  = useRef(scriptData);
  const clipsRef       = useRef(clips);
  const textOvlRef       = useRef(textOverlays);
  const globalTextStyleRef = useRef(globalTextStyle);
  const audioRef2        = useRef(audioConfig);
  const effectsRef     = useRef(effectsConfig);
  const captionCfgRef  = useRef(captionConfig);
  const aspectRef      = useRef(aspectRatio);
  const thumbnailRef   = useRef(thumbnailUrl);
  const thumbDesignRef = useRef(thumbnailDesign);
  const briefRef       = useRef(briefingData);
  const ctaRef         = useRef(ctaText);

  // Sync refs on every render
  onSaveRef.current     = onSave;
  dayRef.current        = day;
  postTitleRef.current  = postTitle;
  toneRef.current       = tone;
  captionRef.current    = caption;
  hashtagsRef.current   = hashtags;
  scriptDataRef.current = scriptData;
  clipsRef.current      = clips;
  textOvlRef.current         = textOverlays;
  globalTextStyleRef.current = globalTextStyle;
  audioRef2.current          = audioConfig;
  effectsRef.current    = effectsConfig;
  captionCfgRef.current = captionConfig;
  aspectRef.current     = aspectRatio;
  thumbnailRef.current  = thumbnailUrl;
  thumbDesignRef.current = thumbnailDesign;
  briefRef.current      = briefingData;
  ctaRef.current        = ctaText;

  // ── Derived ───────────────────────────────────────────────────────────────
  const platform   = PLATFORM_CFG[day?.platform] || PLATFORM_CFG.instagram;
  const brandColors = useMemo(() => ({
    primary:   businessData?.brandDna?.primaryColour   || '#3B82F6',
    secondary: businessData?.brandDna?.secondaryColour || '#1E40AF',
    accent:    businessData?.brandDna?.accentColour    || '#F59E0B',
  }), [businessData?.brandDna]);
  const activeAR = ASPECT_RATIOS.find(r => r.id === aspectRatio) || ASPECT_RATIOS[0];

  // ── Lock body scroll + cleanup on unmount ────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      // Stop UI music player
      const aud = audioRef.current;
      if (aud) { try { aud.pause(); aud.src = ''; } catch (e) {} }
      // Stop any in-progress export audio (moodExportEl may still be playing)
      const expAud = exportMusicRef.current;
      if (expAud) { try { expAud.pause(); expAud.src = ''; } catch (e) {} }
      exportMusicRef.current = null;
    };
  }, []);

  // ── Auto-load briefing ────────────────────────────────────────────────────
  useEffect(() => {
    if (briefingData) return; // already have it
    const dayKey = day?.day || 'new';
    const saved = localStorage.getItem(`vpe_autosave_day${dayKey}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.brief) { setBriefingData(parsed.brief); return; }
      } catch {}
    }
    loadBriefing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Restore from localStorage ─────────────────────────────────────────────
  useEffect(() => {
    const dayKey = day?.day || 'new';
    const saved = localStorage.getItem(`vpe_autosave_day${dayKey}`);
    if (!saved) return;
    try {
      const p = JSON.parse(saved);
      if (p.postTitle  && !postTitle)    setPostTitle(p.postTitle);
      if (p.caption    && !caption)      setCaption(p.caption);
      if (p.hashtags   && !hashtags.length) setHashtags(p.hashtags);
      if (p.scriptData && !scriptData)   setScriptData(p.scriptData);
      if (p.textOverlays)                setTextOverlays(p.textOverlays);
      if (p.audioConfig)                 setAudioConfig(p.audioConfig);
      if (p.effectsConfig)               setEffectsConfig(p.effectsConfig);
      if (p.captionConfig)               setCaptionConfig(p.captionConfig);
      if (p.aspectRatio)                 setAspectRatio(p.aspectRatio);
      if (p.ctaText)                     setCtaText(p.ctaText);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Briefing loading phases animation ─────────────────────────────────────
  useEffect(() => {
    if (!isLoadingBrief) return;
    const id = setInterval(() => setBriefPhase(p => (p + 1) % BRIEFING_PHASES.length), 1800);
    return () => clearInterval(id);
  }, [isLoadingBrief]);

  // ── Canvas text-wrap utility ──────────────────────────────────────────────
  const wrapCanvasText = useCallback((ctx, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let line    = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width <= maxWidth) {
        line = test;
      } else {
        if (line) lines.push(line);
        line = word;
      }
    }
    if (line) lines.push(line);
    return lines.length ? lines : [text];
  }, []);

  // ── Canvas overlay for text overlays while playing ────────────────────────
  const drawOverlays = useCallback((t) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const w = canvas.width, h = canvas.height;

    // ── Cover image: draw as full-frame overlay for first 3 s ──────────────
    if (coverImgRef.current && t < 3) {
      const fade = t > 2.5 ? Math.max(0, 1 - (t - 2.5) / 0.5) : 1; // fade out last 0.5s
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.drawImage(coverImgRef.current, 0, 0, w, h);
      ctx.restore();
    }

    const posCounts = {};
    const maxTextW = w * 0.88; // 88% of frame width — keeps text inside frame

    textOvlRef.current.forEach(ov => {
      if (t < ov.startTime || t > ov.startTime + ov.duration) return;
      const elapsed   = t - ov.startTime;
      const animIn    = Math.min(1, elapsed / 0.4);
      const remaining = ov.startTime + ov.duration - t;
      const fadeOut   = remaining < 0.35 ? Math.max(0, remaining / 0.35) : 1;
      ctx.save();
      ctx.globalAlpha = animIn * fadeOut;

      const pos    = ov.position || 'bottom';
      const posIdx = posCounts[pos] || 0;
      posCounts[pos] = posIdx + 1;
      const baseY  = pos === 'top' ? 0.08 : pos === 'center' ? 0.44 : 0.76;
      const yRatio = baseY + posIdx * 0.12;
      let cx = w / 2, cy = h * yRatio;

      // Animation offset
      const anim = ov.animation || 'fade';
      if (anim === 'slideUp')   cy += (1 - animIn) * 40;
      if (anim === 'slideLeft') cx -= (1 - animIn) * 50;
      if (anim === 'scale' || anim === 'bounce') {
        const sc = 0.5 + animIn * 0.5;
        ctx.translate(cx, cy); ctx.scale(sc, sc); ctx.translate(-cx, -cy);
      }
      if (anim === 'zoomIn') {
        const sc = 0.3 + animIn * 0.7;
        ctx.translate(cx, cy); ctx.scale(sc, sc); ctx.translate(-cx, -cy);
      }
      if (anim === 'flipDown') {
        const scY = animIn;
        ctx.translate(cx, cy); ctx.scale(1, scY); ctx.translate(-cx, -cy);
      }
      if (anim === 'reveal')    cx += (1 - animIn) * 80;
      if (anim === 'glitch' && animIn < 0.85) {
        cx += (Math.random() - 0.5) * 6 * (1 - animIn);
        cy += (Math.random() - 0.5) * 4 * (1 - animIn);
      }

      const fs   = Math.round((ov.fontSize || 48) * h / 1080);
      const fw   = ov.bold ? '800' : '600';
      ctx.font   = `${fw} ${fs}px ${ov.fontFamily || 'Poppins,Arial,sans-serif'}`;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';

      // Word-wrap
      const lines    = wrapCanvasText(ctx, ov.text, maxTextW);
      const lineH    = fs * 1.3;
      const totalH   = lines.length * lineH;
      const padX     = fs * 0.5, padY = fs * 0.3;
      const boxW     = Math.min(maxTextW + padX * 2, w);
      const boxH     = totalH + padY * 2;
      const boxTop   = cy - boxH / 2;

      // Background
      const bg = ov.background || 'none';
      const brandCol = ov.brandColor || brandColors.primary || '#3B82F6';
      if (bg === 'brandBar' || bg === 'bar') {
        ctx.fillStyle = bg === 'brandBar' ? brandCol : 'rgba(0,0,0,0.62)';
        ctx.fillRect(0, boxTop, w, boxH);
        // Thin accent line at bottom of brand bar
        if (bg === 'brandBar') {
          ctx.fillStyle = 'rgba(255,255,255,0.25)';
          ctx.fillRect(0, boxTop + boxH - 2, w, 2);
        }
      } else if (bg === 'box' || bg === 'brandBox') {
        ctx.fillStyle = bg === 'brandBox' ? brandCol + 'DD' : 'rgba(0,0,0,0.68)';
        if (ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(cx - boxW / 2, boxTop, boxW, boxH, 10);
          ctx.fill();
        } else {
          ctx.fillRect(cx - boxW / 2, boxTop, boxW, boxH);
        }
      }

      // Multi-layer text: stroke → fill
      ctx.shadowColor   = 'rgba(0,0,0,0.95)';
      ctx.shadowBlur    = 12;
      ctx.shadowOffsetY = 2;
      ctx.strokeStyle   = 'rgba(0,0,0,0.55)';
      ctx.lineWidth     = fs * 0.055;
      ctx.fillStyle     = ov.color || '#ffffff';

      lines.forEach((line, li) => {
        const ly = cy - totalH / 2 + li * lineH + lineH / 2;
        ctx.strokeText(line, cx, ly);
        ctx.shadowBlur = 8;
        ctx.fillText(line, cx, ly);
        ctx.shadowBlur = 12;
      });
      ctx.restore();
    });
  }, [wrapCanvasText, brandColors]);

  // Playback animation loop
  useEffect(() => {
    if (!isPlaying) return;
    const loop = () => {
      const vid = videoRef.current;
      if (vid) {
        setCurrentTime(vid.currentTime);
        drawOverlays(vid.currentTime);
      }
      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animRef.current);
  }, [isPlaying, drawOverlays]);

  // ── triggerAutoSave (defined BEFORE any function that calls it) ───────────
  const triggerAutoSave = useCallback(() => {
    setAutoSaveStatus('saving');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      const dayKey = dayRef.current?.day || 'new';
      const payload = {
        postTitle:   postTitleRef.current,
        tone:        toneRef.current,
        caption:     captionRef.current,
        hashtags:    hashtagsRef.current,
        scriptData:  scriptDataRef.current,
        textOverlays: textOvlRef.current,
        audioConfig:  audioRef2.current,
        effectsConfig: effectsRef.current,
        captionConfig: captionCfgRef.current,
        aspectRatio:  aspectRef.current,
        ctaText:      ctaRef.current,
        brief:        briefRef.current,
        savedAt:      Date.now(),
      };

      // 1. localStorage
      try {
        localStorage.setItem(`vpe_autosave_day${dayKey}`, JSON.stringify(payload));
      } catch {}

      // 2. Push to parent (DB)
      if (typeof onSaveRef.current === 'function') {
        try {
          await onSaveRef.current(dayRef.current?.day, {
            postTitle:       postTitleRef.current,
            generatedContent: scriptDataRef.current?.voiceover || '',
            caption:         captionRef.current,
            hashtags:        hashtagsRef.current,
            tone:            toneRef.current,
            brief:           briefRef.current,
            imageUrl:        thumbnailRef.current,
            designData:      thumbDesignRef.current,
            videoData: {
              scriptData:      scriptDataRef.current,
              scriptApproved:  scriptApproved,
              clips:           clipsRef.current.map(c => ({ id: c.id, name: c.name, duration: c.duration, trimStart: c.trimStart, trimEnd: c.trimEnd, label: c.label, enabled: c.enabled })),
              textOverlays:    textOvlRef.current,
              globalTextStyle: globalTextStyleRef.current,
              audioConfig:     audioRef2.current,
              effectsConfig:   effectsRef.current,
              captionConfig:   captionCfgRef.current,
              aspectRatio:     aspectRef.current,
              coverImageUrl:   coverImageUrl,
            },
          });
        } catch (e) {
          console.warn('[VideoEditor] Auto-save DB push failed:', e.message);
        }
      }

      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus('idle'), 2500);
    }, 2000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Briefing ──────────────────────────────────────────────────────────────
  const loadBriefing = useCallback(async () => {
    if (!postTitle && !day?.postTitle) return;
    setIsLoadingBrief(true);
    try {
      const result = await businessAPI.generateTaskBriefing({
        dayData: {
          dayNumber: day?.day,
          postTitle: postTitle || day?.postTitle,
          contentType: day?.contentType,
          platform: day?.platform,
          pillar: day?.pillar,
          format: day?.format,
          week: day?.week,
          isVideo: true,
        },
        businessData,
        strategyData,
      });
      if (result?.briefing) {
        setBriefingData(result.briefing);
        briefRef.current = result.briefing;
        triggerAutoSave();
      }
    } catch (e) {
      console.error('Briefing load error:', e);
    } finally {
      setIsLoadingBrief(false);
    }
  }, [postTitle, day, businessData, strategyData, triggerAutoSave]);

  // ── Script generation ─────────────────────────────────────────────────────
  const handleGenerateScript = useCallback(async () => {
    if (!postTitle.trim()) return;
    setIsGenScript(true);
    try {
      const result = await businessAPI.generateVideoScript({
        postTitle,
        contentType: day?.contentType,
        platform:    day?.platform,
        format:      day?.format,
        businessName: businessData?.businessName,
        industry:    businessData?.industry || businessData?.businessType,
        tone,
        brandDna:    businessData?.brandDna || {},
        targetDuration,
      });
      if (result?.script) {
        setScriptData(result.script);
        setCaption(result.script.caption || '');
        setHashtags(result.script.hashtags || []);
        setCtaText(result.script.cta || 'Book Now');
        setAudioConfig(prev => ({ ...prev, mood: result.script.musicMood || prev.mood }));
        // Pre-populate text overlays from hook + cta
        const newOverlays = [];
        if (result.script.hook) {
          newOverlays.push({ id: `ov_hook_${Date.now()}`, text: result.script.hook, startTime: 0, duration: 3, position: 'top', fontSize: 32, fontFamily: 'Poppins', color: '#ffffff', bold: true, background: 'none', animation: 'fade' });
        }
        if (result.script.cta) {
          newOverlays.push({ id: `ov_cta_${Date.now()}`, text: result.script.cta, startTime: Math.max(0, targetDuration - 4), duration: 4, position: 'bottom', fontSize: 26, fontFamily: 'Poppins', color: '#ffffff', bold: true, background: 'box', animation: 'slideUp' });
        }
        if (newOverlays.length) setTextOverlays(newOverlays);
        triggerAutoSave();
      }
    } catch (e) {
      console.error('Script generation error:', e);
    } finally {
      setIsGenScript(false);
    }
  }, [postTitle, day, businessData, tone, targetDuration, triggerAutoSave]);

  const handleGenerateHooks = useCallback(async () => {
    if (!postTitle.trim()) return;
    setIsGenHooks(true);
    try {
      const result = await businessAPI.generateVideoHooks({
        postTitle,
        contentType: day?.contentType,
        platform:    day?.platform,
        businessName: businessData?.businessName,
        industry:    businessData?.industry || businessData?.businessType,
        tone,
        brandDna:    businessData?.brandDna || {},
      });
      if (result?.hooks) {
        setHookOptions(result.hooks);
        setSelectedHookIdx(0);
        if (result.ctaText) setCtaText(result.ctaText);
      }
    } catch (e) {
      console.error('Hook generation error:', e);
      setHookOptions(['This will change everything', 'What nobody tells you', 'Wait for the result', 'The secret behind this', 'Real before and after']);
    } finally {
      setIsGenHooks(false);
    }
  }, [postTitle, day, businessData, tone]);

  // ── Clip management ───────────────────────────────────────────────────────
  const handleClipUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    files.forEach(file => {
      // Create a blob URL and keep it alive — do NOT revoke until the clip is removed
      const url = URL.createObjectURL(file);
      const vid = document.createElement('video');
      vid.preload = 'metadata';
      vid.onloadedmetadata = () => {
        const dur = vid.duration;
        // NOTE: deliberately NOT revoking url here — it stays alive for the player
        const newClip = {
          id: `clip_${Date.now()}_${Math.random().toString(36).slice(2)}`,
          name: file.name,
          blobUrl: url,
          duration: dur,
          trimStart: 0,
          trimEnd: dur,
          label: `Clip ${file.name.slice(0, 20)}`,
          enabled: true,
        };
        setClips(prev => {
          const updated = [...prev, newClip];
          clipsRef.current = updated;
          return updated;
        });
        triggerAutoSave();
      };
      vid.src = url;
    });
    e.target.value = '';
  }, [triggerAutoSave]);

  const enabledClips = useMemo(() => clips.filter(c => c.enabled), [clips]);
  const totalClipDuration = useMemo(() => enabledClips.reduce((s, c) => s + (c.trimEnd - c.trimStart), 0), [enabledClips]);

  // ── Sync video player when first enabled clip changes ────────────────────
  const prevFirstBlobUrl = useRef(null);
  useEffect(() => {
    const vid = videoRef.current;
    const firstClip = enabledClips[0];
    if (!vid || !firstClip?.blobUrl) return;
    if (prevFirstBlobUrl.current !== firstClip.blobUrl) {
      prevFirstBlobUrl.current = firstClip.blobUrl;
      vid.src = firstClip.blobUrl;
      vid.load();
      vid.currentTime = firstClip.trimStart || 0;
    }
  }, [enabledClips]);

  // ── Video controls ────────────────────────────────────────────────────────
  const handleVideoLoaded = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const dur = isFinite(vid.duration) ? vid.duration : 0;
    setTotalDuration(dur);
    // Resize canvas to match actual video resolution
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width  = vid.videoWidth  || 1080;
      canvas.height = vid.videoHeight || 1920;
    }
    // Set volume immediately
    vid.volume = Math.max(0, Math.min(1, (audioRef2.current.videoVolume ?? 100) / 100));
  }, []);


  // ── Load Google Fonts for text overlays ─────────────────────────────────
  useEffect(() => {
    const families = FONT_OPTIONS.map(f => f.value.replace(/ /g, '+')).join('|');
    const id = 'vpe-google-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id   = id;
      link.rel  = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?${FONT_OPTIONS.map(f => `family=${f.value.replace(/ /g, '+')}:wght@400;600;700;800`).join('&')}&display=swap`;
      document.head.appendChild(link);
    }
  }, []);

  // ── Pre-load cover image for canvas drawing ──────────────────────────────
  useEffect(() => {
    if (!coverImageUrl) { coverImgRef.current = null; return; }
    const img = new Image();
    img.onload = () => { coverImgRef.current = img; };
    img.src = coverImageUrl;
  }, [coverImageUrl]);

  // ── Apply global text style to all overlays ──────────────────────────────
  // Individual per-overlay values are overwritten only when globalTextStyle changes.
  // Per-overlay colour, position, background, animation remain untouched.
  const isFirstGlobalRender = useRef(true);
  useEffect(() => {
    if (isFirstGlobalRender.current) { isFirstGlobalRender.current = false; return; }
    setTextOverlays(prev => prev.map(ov => ({
      ...ov,
      fontFamily: globalTextStyle.fontFamily,
      fontSize:   globalTextStyle.fontSize,
      bold:       globalTextStyle.bold,
    })));
    triggerAutoSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalTextStyle.fontFamily, globalTextStyle.fontSize, globalTextStyle.bold]);

  // ── Pre-load mood MP3 whenever mood changes ───────────────────────────────
  // Loading the file early means readyState >= 2 by the time the user presses Play
  // so startMusic() can play the real file immediately (no procedural gap at all).
  useEffect(() => {
    const aud = audioRef.current;
    if (!aud || customMusicFile || !audioConfig.mood || audioConfig.mood === 'none') return;
    const moodFile = `/audio/moods/${audioConfig.mood}.mp3`;
    aud.preload = 'auto';
    aud.src     = moodFile;
    aud.load();
    aud.onerror = () => {
      aud.removeAttribute('src');
      console.warn(`[Music] File not found: ${moodFile} — no music will play for this mood`);
    };
    aud.oncanplaythrough = () => {
      console.log(`[Music] ✅ Pre-loaded: ${moodFile} (readyState ${aud.readyState})`);
    };
    return () => {
      // If mood changes mid-play, stop current file (startMusic will restart)
      try { aud.pause(); } catch (e) {}
    };
  }, [audioConfig.mood, customMusicFile]);

  // ── Music helper: plays real files only — /audio/moods/<mood>.mp3 or custom upload ──
  // Called synchronously inside click handlers so browser autoplay policy is satisfied.
  const startMusic = useCallback(() => {
    const aud  = audioRef.current;
    const mood = audioConfig.mood;
    const vol  = audioConfig.volume;
    if (!aud || mood === 'none') return;

    // Custom uploaded file
    if (customMusicFile) {
      aud.volume = vol / 100;
      aud.loop   = true;
      aud.play().catch(e => console.warn('[Music] Custom file play failed:', e));
      return;
    }

    // Mood file from /audio/moods/ — ensure src is set, then call play().
    // play() is valid even before readyState >= 2: the browser loads + plays as one op.
    // Calling play() inside a user-gesture handler (click) is always autoplay-policy safe.
    const moodFile = `/audio/moods/${mood}.mp3`;
    if (!aud.src || !aud.src.endsWith(`${mood}.mp3`)) {
      // Src not set or wrong mood — assign it now
      aud.preload = 'auto';
      aud.src     = moodFile;
    }
    aud.volume = Math.min(0.55, vol / 100 * 0.6);
    aud.loop   = true;
    aud.play().catch(e => console.warn('[Music] Mood file play failed:', e.message));
  }, [audioConfig.mood, audioConfig.volume, customMusicFile]);

  const stopMusic = useCallback(() => {
    const aud = audioRef.current;
    if (aud) { try { aud.pause(); } catch (e) {} }
  }, []);

  // ── Music sync: update volume while playing (don't stop on mood/volume change) ──
  useEffect(() => {
    // Only update live volume — never stop music here (stopping happens in explicit handlers)
    if (!isPlaying) return;
    const aud = audioRef.current;
    if (aud && !aud.paused) {
      aud.volume = Math.min(0.55, audioConfig.volume / 100 * 0.6);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioConfig.mood, audioConfig.volume]);

  const togglePlay = useCallback(() => {
    const vid = videoRef.current;
    if (!vid || !enabledClips.length) return;

    if (!vid.paused) {
      // ── PAUSE (use DOM state — not React isPlaying — to avoid stale closures) ──
      playPendingRef.current = false;
      vid.pause();           // onPause handler sets setIsPlaying(false)
      stopMusic();
    } else {
      // ── PLAY ──────────────────────────────────────────────────────────────
      if (playPendingRef.current) return; // already in-flight, ignore double-click
      playPendingRef.current = true;
      setMusicPreviewPlaying(false);

      // ⚠️ startMusic() MUST be in the click handler (user-gesture context for audio)
      startMusic();

      vid.volume = Math.max(0, Math.min(1, (audioConfig.videoVolume ?? 100) / 100));
      vid.play()
        .catch(() => {
          vid.muted = true;   // retry muted — onPlay handler will still fire
          vid.play().catch(() => {
            playPendingRef.current = false;
            stopMusic();      // real failure — give up
          });
        });
      // NOTE: setIsPlaying(true) is handled by the video's onPlay event
    }
  }, [enabledClips.length, audioConfig.videoVolume, startMusic, stopMusic]);

  const handleSeek = useCallback((pct) => {
    const vid = videoRef.current;
    if (!vid || !totalDuration) return;
    const t = pct * totalDuration;
    vid.currentTime = t;
    setCurrentTime(t);
    drawOverlays(t);
    // Keep music in sync when scrubbing
    if (isPlaying) {
      const aud = audioRef.current;
      if (aud && !aud.paused && aud.duration) {
        // Seek music to approximate position in the loop
        aud.currentTime = t % aud.duration;
      } else if (aud && aud.paused) {
        startMusic();
      }
    }
  }, [totalDuration, drawOverlays, isPlaying, startMusic]);

  // ── Capture frame as thumbnail ────────────────────────────────────────────
  const handleCaptureFrame = useCallback(() => {
    const vid = videoRef.current;
    const tc  = thumbCanvasRef.current;
    if (!vid || !tc) return;
    tc.width  = vid.videoWidth  || 1080;
    tc.height = vid.videoHeight || 1920;
    tc.getContext('2d').drawImage(vid, 0, 0, tc.width, tc.height);
    const dataUrl = tc.toDataURL('image/png');
    setThumbnailUrl(dataUrl);
    setThumbnailMode('frame');
    triggerAutoSave();
  }, [triggerAutoSave]);

  // ── Text overlay management ───────────────────────────────────────────────
  const addTextOverlay = useCallback(() => {
    const t = currentTime || 0;
    const id = `ov_${Date.now()}`;
    setTextOverlays(prev => [...prev, {
      id, text: 'Tap to edit', startTime: t, duration: 4,
      position: 'center',
      fontSize:   globalTextStyle.fontSize,
      fontFamily: globalTextStyle.fontFamily,
      bold:       globalTextStyle.bold,
      color: '#ffffff', background: 'box', animation: 'fade'
    }]);
    setEditingOverlayId(id);
    triggerAutoSave();
  }, [currentTime, globalTextStyle, triggerAutoSave]);

  const updateOverlay = useCallback((id, updates) => {
    setTextOverlays(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
    triggerAutoSave();
  }, [triggerAutoSave]);

  const removeOverlay = useCallback((id) => {
    setTextOverlays(prev => prev.filter(o => o.id !== id));
    if (editingOverlayId === id) setEditingOverlayId(null);
    triggerAutoSave();
  }, [editingOverlayId, triggerAutoSave]);

  // ── Auto-generate text overlays from script (Music Only mode) ────────────
  const generateTextOverlaysFromScript = useCallback(() => {
    const sd  = scriptDataRef.current;
    if (!sd) return;
    const dur = totalDuration || totalClipDuration || 30;
    const pc  = brandColors.primary   || '#3B82F6';
    const ac  = brandColors.accent    || '#F59E0B';

    // Helper: extract a clean complete phrase — no mid-word cuts, no repetitions
    const seen = new Set();
    const clean = (raw = '') => {
      const text = (raw || '').trim().replace(/\s+/g, ' ');
      if (!text) return null;

      // Short enough — use as-is
      if (text.length <= 60) {
        const key = text.toLowerCase();
        if (seen.has(key)) return null;
        seen.add(key);
        return text;
      }

      // Try to get a complete sentence (ends . ! ?) within 70 chars
      const sm = text.match(/^(.{10,70}?[.!?])\s/);
      if (sm) {
        const phrase = sm[1].trim();
        const key    = phrase.toLowerCase();
        if (seen.has(key)) return null;
        seen.add(key);
        return phrase;
      }

      // No sentence boundary — cut at last word boundary before 55 chars
      const slice  = text.slice(0, 55);
      const lsp    = slice.lastIndexOf(' ');
      const phrase = (lsp > 8 ? slice.slice(0, lsp) : slice).trim();
      if (!phrase) return null;
      const key = phrase.toLowerCase();
      if (seen.has(key)) return null;
      seen.add(key);
      return phrase;
    };

    const overlays = [];
    const ts = Date.now();

    // ── 1. HOOK — full width colour bar at top (brand primary) ──────────────
    const hookText = clean(sd.hook);
    if (hookText) {
      overlays.push({
        id:         `ov_hook_${ts}`,
        text:       hookText,
        startTime:  0.3,
        duration:   3.5,
        position:   'top',
        fontSize:   48,
        fontFamily: 'Poppins',
        color:      '#ffffff',
        bold:       true,
        background: 'brandBar',    // custom: full-width brand-colour bar
        brandColor: pc,
        animation:  'scale',
      });
    }

    // ── 2. SCENE cards — alternate positions, brand accent text ─────────────
    const scenes = (sd.scenes || []).slice(0, 5); // cap at 5 to avoid wall of text
    const spacing = Math.max(4, (dur - 10) / Math.max(scenes.length, 1));
    scenes.forEach((scene, i) => {
      const rawText = scene.textOverlay || scene.hook || scene.voiceover || '';
      const text    = clean(rawText);
      if (!text) return;
      const t       = 4.5 + i * spacing;
      if (t >= dur - 4) return; // don't push past the end
      const positions = ['center', 'bottom', 'center', 'bottom', 'center'];
      const animations = ['slideUp', 'scale', 'slideLeft', 'bounce', 'slideUp'];
      overlays.push({
        id:         `ov_scene_${ts}_${i}`,
        text,
        startTime:  Math.min(t, dur - 4.5),
        duration:   3.2,
        position:   positions[i % positions.length],
        fontSize:   42,
        fontFamily: 'Poppins',
        color:      '#ffffff',
        bold:       true,
        background: i % 2 === 0 ? 'box' : 'brandBox',  // alternate plain/brand box
        brandColor: pc,
        animation:  animations[i % animations.length],
      });
    });

    // ── 3. CTA — solid brand-colour bar at bottom ────────────────────────────
    const ctaText = clean(sd.cta);
    if (ctaText) {
      overlays.push({
        id:         `ov_cta_${ts}`,
        text:       ctaText,
        startTime:  Math.max(1, dur - 5),
        duration:   5,
        position:   'bottom',
        fontSize:   36,
        fontFamily: 'Poppins',
        color:      '#ffffff',
        bold:       true,
        background: 'brandBar',
        brandColor: pc,
        animation:  'slideUp',
      });
    }

    // Replace ALL existing overlays (prevents duplicates on re-run)
    setTextOverlays(overlays);
    setCaptionConfig(prev => ({ ...prev, style: 'word-by-word' }));
  }, [totalDuration, totalClipDuration, brandColors]);

  // ── Complete / Save ───────────────────────────────────────────────────────
  const handleMarkDone = useCallback(async () => {
    const dayKey = dayRef.current?.day;
    if (typeof onSaveRef.current === 'function') {
      await onSaveRef.current(dayKey, {
        postTitle,
        generatedContent: scriptData?.voiceover || '',
        caption, hashtags, tone,
        status: 'completed',
        completedAt: new Date().toISOString(),
        brief: briefingData,
        imageUrl: thumbnailUrl,
        designData: thumbnailDesign,
        videoData: {
          scriptData,
          scriptApproved: true,
          clips: clips.map(c => ({ id: c.id, name: c.name, duration: c.duration, trimStart: c.trimStart, trimEnd: c.trimEnd, label: c.label, enabled: c.enabled })),
          textOverlays, audioConfig, effectsConfig, captionConfig, aspectRatio, outputMode,
        },
      });
    }
    onClose?.();
  }, [postTitle, scriptData, caption, hashtags, tone, briefingData, thumbnailUrl, thumbnailDesign, clips, textOverlays, audioConfig, effectsConfig, captionConfig, aspectRatio, onClose]);

  const handlePublishConfirm = useCallback(async () => {
    const platformKey = dayRef.current?.platform || 'instagram';
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const isLocalhost = API_URL.includes('localhost') || API_URL.includes('127.0.0.1');

    setIsPublishing(true);
    setPublishError('');
    try {
      if (isLocalhost) {
        throw new Error('Instagram and Facebook cannot fetch media from localhost. Deploy the app to a live server first, then publish from there.');
      }

      // WebM is handled server-side via ffmpeg conversion — no client block needed

      const token = localStorage.getItem('token');
      let videoUrl = null;

      if (exportedBlobRef.current) {
        const ext = exportedBlobRef.current.type.includes('mp4') ? '.mp4' : '.webm';
        const file = new File([exportedBlobRef.current], `video${ext}`, { type: exportedBlobRef.current.type });
        const fd = new FormData();
        fd.append('video', file);
        const uploadRes = await fetch(`${API_URL}/api/social/upload-video`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const uploadData = await uploadRes.json();
        if (!uploadData.success) throw new Error(uploadData.message || 'Video upload failed');
        videoUrl = uploadData.videoUrl.startsWith('http') ? uploadData.videoUrl : `${API_URL}${uploadData.videoUrl}`;
      }

      const fullCaption = hashtags.length
        ? `${caption}\n\n${hashtags.map(h => `#${h.replace(/^#/, '')}`).join(' ')}`
        : caption;

      const postRes = await fetch(`${API_URL}/api/social/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ platform: platformKey, caption: fullCaption, videoUrl, dayNumber: dayRef.current?.day }),
      });
      const postData = await postRes.json();
      if (!postData.success) throw new Error(postData.message || 'Publishing failed');

      // Save to DB without calling onClose() — success modal handles the close
      const dayKey = dayRef.current?.day;
      if (typeof onSaveRef.current === 'function') {
        await onSaveRef.current(dayKey, {
          postTitle: postTitleRef.current,
          generatedContent: scriptDataRef.current?.voiceover || '',
          caption: captionRef.current,
          hashtags: hashtagsRef.current,
          tone: toneRef.current,
          status: 'completed',
          completedAt: new Date().toISOString(),
          brief: briefRef.current,
          imageUrl: thumbnailRef.current,
          designData: thumbDesignRef.current,
          videoData: {
            scriptData: scriptDataRef.current,
            scriptApproved: true,
            clips: clipsRef.current.map(c => ({ id: c.id, name: c.name, duration: c.duration, trimStart: c.trimStart, trimEnd: c.trimEnd, label: c.label, enabled: c.enabled })),
            textOverlays: textOvlRef.current,
            audioConfig: audioRef2.current,
            effectsConfig: effectsRef.current,
            captionConfig: captionCfgRef.current,
            aspectRatio: aspectRef.current,
            outputMode: outputMode,
          },
        });
      }
      setPublishSuccess(true);
      setShowPublishConfirm(false);
    } catch (err) {
      setPublishError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  }, [caption, hashtags, outputMode]);

  // ── AI Auto-Edit ──────────────────────────────────────────────────────────
  const handleAutoEdit = useCallback(async () => {
    if (!enabledClips.length) return;
    setIsAutoEditing(true);
    setAutoEditDone(false);
    setAutoEditError('');
    setAutoEditStep(0);

    // Animate through steps while waiting for AI
    let stepIdx = 0;
    const stepTimer = setInterval(() => {
      stepIdx = Math.min(stepIdx + 1, AUTO_EDIT_STEPS.length - 1);
      setAutoEditStep(stepIdx);
    }, 700);

    try {
      const plan = await businessAPI.generateVideoEditPlan({
        postTitle,
        platform:      day?.platform || 'instagram',
        format:        day?.format   || 'reel',
        tone,
        videoDuration: totalDuration || totalClipDuration || 30,
        scriptData,
        businessName:  businessData?.businessName  || '',
        industry:      businessData?.industry      || '',
        brandDna:      businessData?.brandDna      || {},
        contentType:   day?.contentType            || 'educational',
      });

      clearInterval(stepTimer);
      setAutoEditStep(AUTO_EDIT_STEPS.length - 1);

      if (!plan?.plan && !plan?.aspectRatio) {
        throw new Error('Invalid plan returned from AI');
      }

      const editPlan = plan.plan || plan;

      // ── Apply all settings atomically ──────────────────────────────────
      if (editPlan.aspectRatio)   setAspectRatio(editPlan.aspectRatio);
      if (editPlan.effectsConfig) setEffectsConfig(editPlan.effectsConfig);
      if (editPlan.captionConfig) setCaptionConfig(editPlan.captionConfig);
      if (editPlan.musicMood)     setAudioConfig(prev => ({ ...prev, mood: editPlan.musicMood }));
      if (editPlan.textOverlays?.length) setTextOverlays(editPlan.textOverlays);
      if (editPlan.captionConfig?.style && editPlan.captionConfig.style !== 'none') {
        // Auto-switch to captions tab to show the result
      }

      setAutoEditSummary(editPlan.autoEditSummary || 'Auto-edit applied successfully.');
      setAutoEditDone(true);

      // Switch to text tab so user sees overlays
      setControlTab('text');
      triggerAutoSave();
    } catch (err) {
      clearInterval(stepTimer);
      setAutoEditError(err.message || 'Auto-edit failed. Please try again.');
    } finally {
      setIsAutoEditing(false);
    }
  }, [enabledClips.length, postTitle, tone, day, totalDuration, totalClipDuration, scriptData,
      businessData, triggerAutoSave]);

  // ── Phone preview: draw overlays on preview canvas ───────────────────────
  const drawPreviewOverlays = useCallback((t) => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const w = canvas.width, h = canvas.height;

    // Cover image overlay for first 3 s
    if (coverImgRef.current && t < 3) {
      const fade = t > 2.5 ? Math.max(0, 1 - (t - 2.5) / 0.5) : 1;
      ctx.save(); ctx.globalAlpha = fade;
      ctx.drawImage(coverImgRef.current, 0, 0, w, h);
      ctx.restore();
    }
    const maxTextW = w * 0.88;
    const posCounts = {};
    textOvlRef.current.forEach(ov => {
      if (t < ov.startTime || t > ov.startTime + ov.duration) return;
      const elapsed = t - ov.startTime;
      const animIn  = Math.min(1, elapsed / 0.4);
      const remaining = ov.startTime + ov.duration - t;
      const fadeOut = remaining < 0.35 ? Math.max(0, remaining / 0.35) : 1;
      ctx.save();
      ctx.globalAlpha = animIn * fadeOut;
      const pos = ov.position || 'bottom';
      const posIdx = posCounts[pos] || 0;
      posCounts[pos] = posIdx + 1;
      const baseY = pos === 'top' ? 0.08 : pos === 'center' ? 0.44 : 0.76;
      let cx = w / 2, cy = h * (baseY + posIdx * 0.12);
      const anim = ov.animation || 'fade';
      if (anim === 'slideUp')   cy += (1 - animIn) * 40;
      if (anim === 'slideLeft') cx -= (1 - animIn) * 50;
      if (anim === 'scale' || anim === 'bounce' || anim === 'zoomIn') {
        const sc = (anim === 'zoomIn' ? 0.2 : 0.5) + animIn * (anim === 'zoomIn' ? 0.8 : 0.5);
        ctx.translate(cx, cy); ctx.scale(sc, sc); ctx.translate(-cx, -cy);
      }
      const fs  = Math.round((ov.fontSize || 48) * h / 1080);
      ctx.font  = `${ov.bold ? '800' : '600'} ${fs}px ${ov.fontFamily || 'Poppins,Arial,sans-serif'}`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const lines = wrapCanvasText(ctx, ov.text, maxTextW);
      const lineH = fs * 1.3, totalH = lines.length * lineH;
      const padX = fs * 0.5, padY = fs * 0.3;
      const boxW = Math.min(maxTextW + padX * 2, w), boxH = totalH + padY * 2;
      const boxTop = cy - boxH / 2;
      const bg = ov.background || 'none';
      const brandCol = ov.brandColor || brandColors.primary || '#3B82F6';
      if (bg === 'brandBar' || bg === 'bar') {
        ctx.fillStyle = bg === 'brandBar' ? brandCol : 'rgba(0,0,0,0.62)';
        ctx.fillRect(0, boxTop, w, boxH);
      } else if (bg === 'box' || bg === 'brandBox') {
        ctx.fillStyle = bg === 'brandBox' ? brandCol + 'DD' : 'rgba(0,0,0,0.68)';
        if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(cx - boxW/2, boxTop, boxW, boxH, 10); ctx.fill(); }
        else ctx.fillRect(cx - boxW/2, boxTop, boxW, boxH);
      }
      ctx.shadowColor = 'rgba(0,0,0,0.95)'; ctx.shadowBlur = 12; ctx.shadowOffsetY = 2;
      ctx.strokeStyle = 'rgba(0,0,0,0.55)'; ctx.lineWidth = fs * 0.055;
      ctx.fillStyle = ov.color || '#ffffff';
      lines.forEach((line, li) => {
        const ly = cy - totalH/2 + li * lineH + lineH/2;
        ctx.strokeText(line, cx, ly); ctx.fillText(line, cx, ly);
      });
      ctx.restore();
    });
  }, [wrapCanvasText, brandColors]);

  useEffect(() => {
    if (!showPreview) {
      if (previewAnimRef.current) cancelAnimationFrame(previewAnimRef.current);
      return;
    }
    const loop = () => {
      const vid = previewVideoRef.current;
      if (vid) { setPreviewCurTime(vid.currentTime); drawPreviewOverlays(vid.currentTime); }
      previewAnimRef.current = requestAnimationFrame(loop);
    };
    previewAnimRef.current = requestAnimationFrame(loop);
    return () => { if (previewAnimRef.current) cancelAnimationFrame(previewAnimRef.current); };
  }, [showPreview, drawPreviewOverlays]);

  // ── Auto-trigger when entering 'edit' section ────────────────────────────
  useEffect(() => {
    if (activeSection !== 'edit' || clipsRef.current.length === 0 || editTriggeredRef.current) return;
    editTriggeredRef.current = true;

    if (outputMode === 'music-only' && scriptDataRef.current) {
      // Music-only: skip AI edit plan, just auto-fill text overlays from script immediately
      generateTextOverlaysFromScript();
      setAutoEditDone(true);
      setControlTab('text');
      triggerAutoSave();
    } else if (!autoEditDone) {
      // Voice+music: run full AI auto-edit plan
      handleAutoEdit();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // ── Export video with all overlays + effects composited ──────────────────
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus,   setExportStatus]   = useState('idle'); // 'idle'|'rendering'|'done'|'error'

  const handleDownloadVideo = useCallback(async () => {
    const clip = enabledClips[0];
    if (!clip?.blobUrl || exportStatus === 'rendering') return;

    // Fallback: no MediaRecorder / captureStream support
    if (!window.MediaRecorder || !HTMLCanvasElement.prototype.captureStream) {
      const a = document.createElement('a');
      a.href = clip.blobUrl;
      a.download = `${(postTitle || 'video').replace(/[^a-z0-9_]/gi, '_')}.mp4`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      return;
    }

    stopMusic(); // silence music while canvas is rendering
    setExportStatus('rendering');
    setExportProgress(0);

    try {
      const AR    = ASPECT_RATIOS.find(r => r.id === aspectRef.current) || ASPECT_RATIOS[0];
      const EXP_H = 1080;
      const EXP_W = Math.round(EXP_H * AR.w / AR.h);

      const exportCanvas = document.createElement('canvas');
      exportCanvas.width  = EXP_W;
      exportCanvas.height = EXP_H;
      const ctx = exportCanvas.getContext('2d');

      // Offscreen video — NO crossOrigin for blob URLs (breaks loading in Chrome)
      const vid = document.createElement('video');
      vid.src          = clip.blobUrl;
      vid.playsInline  = true;
      vid.preload      = 'auto';
      vid.playbackRate = effectsRef.current.speed || 1;
      // Wait for metadata — guard against already-loaded state
      await new Promise((res, rej) => {
        if (vid.readyState >= 1) { res(); return; }
        vid.onloadedmetadata = res;
        vid.onerror = () => rej(new Error('Video load failed'));
        setTimeout(() => rej(new Error('Video load timeout')), 12000);
      });

      const trimStart = clip.trimStart || 0;
      const trimEnd   = Math.min(clip.trimEnd ?? vid.duration, vid.duration);
      vid.currentTime = trimStart;
      // Wait for seek — guard against already-at-position
      await new Promise(res => {
        if (Math.abs(vid.currentTime - trimStart) < 0.1) { res(); return; }
        vid.onseeked = res;
        setTimeout(res, 3000);
      });

      // CSS → canvas filter string
      const filterStr = (() => {
        const p = FILTER_PRESETS.find(f => f.id === effectsRef.current.filter);
        const base = (p?.css && p.css !== 'none') ? p.css : '';
        const adj = [
          effectsRef.current.brightness ? `brightness(${1 + effectsRef.current.brightness})` : '',
          effectsRef.current.contrast   ? `contrast(${1 + effectsRef.current.contrast})`     : '',
          effectsRef.current.saturation ? `saturate(${1 + effectsRef.current.saturation})`   : '',
        ].filter(Boolean).join(' ');
        return [base, adj].filter(Boolean).join(' ') || '';
      })();

      // Text wrap utility — splits text into lines that fit within maxWidth
      const wrapText = (c, text, maxWidth) => {
        const words = text.split(' ');
        const lines = [];
        let line = '';
        for (const word of words) {
          const test = line ? `${line} ${word}` : word;
          if (c.measureText(test).width <= maxWidth) { line = test; }
          else { if (line) lines.push(line); line = word; }
        }
        if (line) lines.push(line);
        return lines.length ? lines : [text];
      };

      // Draw helper — renders one frame to the export canvas
      const renderFrame = (t) => {
        if (filterStr) ctx.filter = filterStr;
        ctx.drawImage(vid, 0, 0, EXP_W, EXP_H);
        ctx.filter = 'none';

        // Hook banner (first 3s) — brand colour bar with wrapped text
        const elapsed = t - trimStart;
        if (elapsed < 3) {
          const hook = (hookOptions[selectedHookIdx] || scriptDataRef.current?.hook || '').trim();
          if (hook) {
            const fs = Math.round(52 * EXP_H / 1080);
            const hookBrandColor = brandColors.primary || '#3B82F6';
            ctx.save();
            ctx.globalAlpha = Math.min(1, 1 - elapsed / 3 + 0.2);
            ctx.font = `800 ${fs}px Poppins,Arial,sans-serif`;
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            const hookLines = wrapText(ctx, hook, EXP_W * 0.88);
            const hookLineH = fs * 1.3;
            const hookBanH = hookLines.length * hookLineH + fs * 0.8;
            ctx.fillStyle = hookBrandColor + 'EE';
            ctx.fillRect(0, 0, EXP_W, hookBanH);
            ctx.shadowColor = 'rgba(0,0,0,0.9)'; ctx.shadowBlur = 10;
            ctx.strokeStyle = 'rgba(0,0,0,0.5)'; ctx.lineWidth = fs * 0.065;
            ctx.fillStyle = '#ffffff';
            hookLines.forEach((ln, li) => {
              const ly = fs * 0.5 + li * hookLineH + hookLineH / 2;
              ctx.strokeText(ln, EXP_W / 2, ly);
              ctx.shadowBlur = 6;
              ctx.fillText(ln, EXP_W / 2, ly);
              ctx.shadowBlur = 10;
            });
            ctx.restore();
          }
        }

        // Text overlays — professional canvas rendering with animation interpolation
        const posCount = {};
        textOvlRef.current.forEach(ov => {
          if (t < ov.startTime || t > ov.startTime + ov.duration) return;
          const animProgress = Math.min(1, (t - ov.startTime) / 0.45); // 0→1 over 450ms
          const fadeOut      = t > ov.startTime + ov.duration - 0.3
            ? Math.max(0, (ov.startTime + ov.duration - t) / 0.3) : 1;
          const pos   = ov.position || 'bottom';
          const pIdx  = posCount[pos] || 0; posCount[pos] = pIdx + 1;
          const baseY = pos === 'top' ? 0.07 : pos === 'center' ? 0.44 : 0.72;
          const yR    = baseY + pIdx * 0.12;
          const fs    = Math.round((ov.fontSize || 64) * EXP_H / 1080);
          const cx    = EXP_W / 2;
          ctx.save();

          // Animate position offset based on animation type
          let offsetY = 0, offsetX = 0, scale = 1;
          const anim = ov.animation || 'fade';
          if (anim === 'slideUp')   { offsetY = (1 - animProgress) * 32; }
          if (anim === 'slideLeft') { offsetX = -(1 - animProgress) * 40; }
          if (anim === 'scale' || anim === 'bounce') {
            scale = 0.55 + animProgress * 0.45;
          }

          const cy = EXP_H * yR + offsetY;
          ctx.globalAlpha = Math.min(animProgress, fadeOut);
          if (scale !== 1) {
            ctx.translate(cx, cy);
            ctx.scale(scale, scale);
            ctx.translate(-cx, -cy);
          }
          if (offsetX !== 0) { ctx.translate(offsetX, 0); }

          const fontWeight = ov.bold ? '800' : '600';
          ctx.font = `${fontWeight} ${fs}px ${ov.fontFamily || 'Poppins,Arial,sans-serif'}`;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';

          // Wrap text + compute block dimensions
          const maxTxtW = EXP_W * 0.88;
          const lines   = wrapText(ctx, ov.text, maxTxtW);
          const lineH   = fs * 1.35;
          const totalH  = lines.length * lineH + fs * 0.65;
          const bw      = maxTxtW + fs * 0.8;
          const exportBrandColor = ov.brandColor || brandColors.primary || '#3B82F6';

          // Background — brandBar / brandBox / box / bar
          if (ov.background === 'brandBar') {
            ctx.fillStyle = exportBrandColor + 'EE';
            ctx.fillRect(0, cy - totalH / 2, EXP_W, totalH);
          } else if (ov.background === 'brandBox') {
            ctx.fillStyle = exportBrandColor + 'DD';
            if (ctx.roundRect) {
              ctx.beginPath(); ctx.roundRect(cx - bw/2, cy - totalH/2, bw, totalH, 14); ctx.fill();
            } else {
              ctx.fillRect(cx - bw/2, cy - totalH/2, bw, totalH);
            }
          } else if (ov.background === 'box') {
            ctx.fillStyle = 'rgba(0,0,0,0.68)';
            if (ctx.roundRect) {
              ctx.beginPath(); ctx.roundRect(cx - bw/2, cy - totalH/2, bw, totalH, 10); ctx.fill();
            } else {
              ctx.fillRect(cx - bw/2, cy - totalH/2, bw, totalH);
            }
          } else if (ov.background === 'bar') {
            ctx.fillStyle = 'rgba(0,0,0,0.58)';
            ctx.fillRect(0, cy - totalH/2, EXP_W, totalH);
          }

          // Multi-layer text rendering per line: shadow → stroke → fill
          ctx.shadowColor   = 'rgba(0,0,0,0.95)';
          ctx.shadowBlur    = 16;
          ctx.shadowOffsetY = 2;
          ctx.strokeStyle   = 'rgba(0,0,0,0.55)';
          ctx.lineWidth     = fs * 0.065;
          ctx.fillStyle     = ov.color || '#ffffff';
          const startLineY  = cy - (lines.length - 1) * lineH / 2;
          lines.forEach((ln, li) => {
            const ly = startLineY + li * lineH;
            ctx.strokeText(ln, cx, ly);
            ctx.shadowBlur = 8;
            ctx.fillText(ln, cx, ly);
            ctx.shadowBlur = 16;
          });

          ctx.restore();
        });

        // CTA (last 4s)
        const clipLen = trimEnd - trimStart;
        if (ctaRef.current && elapsed >= clipLen - 4) {
          const fs = Math.round(44 * EXP_H / 1080);
          ctx.save();
          ctx.fillStyle = (brandColors.primary || '#3B82F6') + 'EE';
          ctx.fillRect(0, EXP_H * 0.905, EXP_W, EXP_H * 0.095);
          ctx.fillStyle = '#fff'; ctx.font = `bold ${fs}px Poppins,Arial,sans-serif`;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.shadowColor = 'rgba(0,0,0,0.7)'; ctx.shadowBlur = 5;
          ctx.fillText(ctaRef.current, EXP_W / 2, EXP_H * 0.952);
          ctx.restore();
        }
      };

      // Try full Web Audio + canvas recording
      let recorder;
      let voiceExportEl = null; // Fresh Audio element for TTS voice — declared outside try so it's accessible at recorder.start()
      let moodExportEl  = null; // Fresh Audio element for mood music — hoisted so stopExport can pause it
      console.log(`[Export] 🎬 Starting export — ${EXP_W}x${EXP_H} | mood: ${audioConfig.mood} | outputMode: ${outputMode}`);

      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        console.log(`[Export] AudioContext created — state: ${audioCtx.state}`);

        // ── Video audio track ──
        const src  = audioCtx.createMediaElementSource(vid);
        const gain = audioCtx.createGain();
        gain.gain.value = (audioRef2.current.videoVolume ?? 100) / 100;
        console.log(`[Export] Video track gain: ${gain.gain.value.toFixed(2)}`);

        const dest = audioCtx.createMediaStreamDestination();
        src.connect(gain); gain.connect(dest); gain.connect(audioCtx.destination);

        // ── Voice track (TTS audio — use fresh element from stored blob URL) ──
        if (outputMode === 'voice+music' && ttsBlobUrlRef.current) {
          try {
            // Always create a fresh Audio element — never reuse the preview element
            // (HTMLMediaElement can only be connected to one AudioContext)
            voiceExportEl = new Audio(ttsBlobUrlRef.current);
            voiceExportEl.preload = 'auto';
            const voiceSource = audioCtx.createMediaElementSource(voiceExportEl);
            const voiceGain   = audioCtx.createGain();
            voiceGain.gain.value = 1.0;  // Full voice volume
            voiceSource.connect(voiceGain);
            voiceGain.connect(dest);
            voiceGain.connect(audioCtx.destination);
            console.log(`[Export] ✅ Voice track added to mixer — fresh audio element from blob URL`);
          } catch (e) {
            console.warn(`[Export] ⚠️  Could not attach TTS voice to mixer: ${e.message}`);
            voiceExportEl = null;
          }
        } else if (outputMode === 'voice+music') {
          console.warn(`[Export] ⚠️  Voice+Music mode but no TTS blob URL — preview your voiceover first, then export`);
        } else {
          console.log(`[Export] Music-Only mode — voice track skipped`);
        }

        // ── Music track — always use a FRESH Audio element so it's not already
        //    connected to another AudioContext from the preview session ─────────
        if (audioConfig.mood !== 'none') {
          const moodFile      = customMusicFile
            ? URL.createObjectURL(customMusicFile)
            : `/audio/moods/${audioConfig.mood}.mp3`;
          const moodGainValue = Math.min(0.45, (audioConfig.volume / 100) * 0.45);
          try {
            moodExportEl = new Audio(moodFile);
            exportMusicRef.current = moodExportEl; // allow unmount cleanup
            moodExportEl.loop    = true;
            moodExportEl.preload = 'auto';
            const moodSource = audioCtx.createMediaElementSource(moodExportEl);
            const moodGain   = audioCtx.createGain();
            moodGain.gain.value  = moodGainValue;
            moodSource.connect(moodGain);
            moodGain.connect(dest);
            moodGain.connect(audioCtx.destination);
            console.log(`[Export] ✅ Music track added — ${audioConfig.mood}`);
          } catch (e) {
            console.warn(`[Export] ⚠️  Could not attach music: ${e.message}`);
            moodExportEl = null;
          }
        } else {
          console.log('[Export] Mood is "none" — no music track');
        }

        const combinedStream = exportCanvas.captureStream(30);
        const audioTracks = dest.stream.getAudioTracks();
        audioTracks.forEach(t => combinedStream.addTrack(t));
        console.log(`[Export] Combined stream — video tracks: ${combinedStream.getVideoTracks().length}, audio tracks: ${combinedStream.getAudioTracks().length}`);

        const mime = ['video/webm;codecs=vp9,opus','video/webm;codecs=vp8,opus','video/webm']
          .find(t => MediaRecorder.isTypeSupported(t)) || 'video/webm';
        console.log(`[Export] MediaRecorder mime: ${mime}`);

        recorder = new MediaRecorder(combinedStream, { mimeType: mime, videoBitsPerSecond: 10_000_000 });
        const chunks = [];
        recorder.ondataavailable = e => { if (e.data?.size > 0) chunks.push(e.data); };
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: mime });
          console.log(`[Export] ✅ Export complete — blob size: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
          exportedBlobRef.current = blob; // keep for publish flow
          const url  = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `${(postTitleRef.current || 'video').replace(/[^a-z0-9_]/gi,'_')}_final.webm`;
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 3000);
          setExportStatus('done'); setExportProgress(100);
          // ← intentionally NOT resetting to 'idle' — button stays green until editor closes
          audioCtx.close();
        };
      } catch (webAudioErr) {
        console.warn(`[Export] ⚠️  Web Audio unavailable (${webAudioErr.message}) — recording canvas video-only (no mixed audio)`);
        // No Web Audio — record canvas video-only
        const mime = ['video/webm;codecs=vp9','video/webm'].find(t => MediaRecorder.isTypeSupported(t)) || 'video/webm';
        recorder = new MediaRecorder(exportCanvas.captureStream(30), { mimeType: mime, videoBitsPerSecond: 8_000_000 });
        const chunks = [];
        recorder.ondataavailable = e => { if (e.data?.size > 0) chunks.push(e.data); };
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: mime });
          console.log(`[Export] ✅ Video-only export complete — blob: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
          exportedBlobRef.current = blob; // keep for publish flow
          const url  = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `${(postTitleRef.current || 'video').replace(/[^a-z0-9_]/gi,'_')}_final.webm`;
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 3000);
          setExportStatus('done'); setExportProgress(100);
          // ← intentionally NOT resetting to 'idle'
        };
      }

      // Export state — shared between render loops and event handlers
      let exportStopped = false;
      const stopExport = () => {
        if (exportStopped) return;
        exportStopped = true;
        vid.pause();
        if (moodExportEl) { try { moodExportEl.pause(); moodExportEl.src = ''; } catch (e) {} }
        exportMusicRef.current = null; // cleared — no longer needs unmount cleanup
        // Give recorder a final 200ms to flush the last chunk before stopping
        setTimeout(() => recorder.stop(), 200);
      };

      // ── Frame render loop — use requestVideoFrameCallback (RVFC) for perfect
      //    frame-by-frame sync (fires exactly once per decoded video frame),
      //    falling back to setInterval(33ms ≈ 30fps) on browsers without RVFC ──
      let exportIntervalId = null;

      const renderTick = () => {
        if (exportStopped) return;
        const t = vid.currentTime;
        renderFrame(t);
        const pct = trimEnd > trimStart ? ((t - trimStart) / (trimEnd - trimStart)) * 100 : 100;
        setExportProgress(Math.round(Math.min(99, pct)));
        if (vid.ended || t >= trimEnd - 0.05) stopExport();
      };

      const stopExportWithCleanup = () => {
        if (exportIntervalId) { clearInterval(exportIntervalId); exportIntervalId = null; }
        stopExport();
      };

      // Also stop via video events as fallback
      vid.onended   = stopExportWithCleanup;
      vid.ontimeupdate = () => { if (vid.currentTime >= trimEnd - 0.05) stopExportWithCleanup(); };

      recorder.start(100);
      vid.volume = 0; // mute export canvas video (audio goes through Web Audio)
      vid.play().catch(err => { console.error('[Export] Video play failed:', err); setExportStatus('error'); });

      // Start music in sync with video
      if (moodExportEl) {
        moodExportEl.currentTime = 0;
        moodExportEl.play().catch(e => console.warn('[Export] Music play failed:', e.message));
        console.log(`[Export] ▶️  Music started — ${audioConfig.mood}`);
      }
      // Start voice audio in sync with video
      if (voiceExportEl) {
        voiceExportEl.currentTime = 0;
        voiceExportEl.play().catch(e => console.warn('[Export] Voice play failed:', e.message));
      }

      if (typeof vid.requestVideoFrameCallback === 'function') {
        // RVFC: fires once per decoded frame — perfect sync, no duplicate frames
        const rvfcLoop = () => {
          if (exportStopped) return;
          renderTick();
          if (!exportStopped) vid.requestVideoFrameCallback(rvfcLoop);
        };
        vid.requestVideoFrameCallback(rvfcLoop);
        console.log('[Export] Using requestVideoFrameCallback for smooth frame capture');
      } else {
        // Fallback: stable 30fps timer — better than RAF which runs at 60fps
        // and captures duplicate video frames causing stutter in output
        exportIntervalId = setInterval(renderTick, 1000 / 30);
        console.log('[Export] Using setInterval(30fps) fallback');
      }

    } catch (err) {
      console.error('Export failed:', err);
      setExportStatus('error');
      setTimeout(() => setExportStatus('idle'), 5000);
    }
  }, [enabledClips, postTitle, exportStatus, textOverlays, hookOptions, selectedHookIdx, brandColors, aspectRatio, stopMusic]);

  // ── Voice synthesis ───────────────────────────────────────────────────────
  // Load voices on mount (Chrome loads them asynchronously)
  useEffect(() => {
    if (!('speechSynthesis' in window)) return;
    const load = () => window.speechSynthesis.getVoices(); // trigger load
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const handlePreviewVoice = useCallback(async () => {
    const rawText = scriptDataRef.current?.voiceover;
    if (!rawText) { setTtsError('Generate your story script first.'); return; }

    // Stop any currently playing TTS audio
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current = null;
    }
    setIsVoicePlaying(false);
    setTtsStatus('loading');
    setTtsError('');

    const token = localStorage.getItem('token');
    console.log(`[Voice] 🎙️  Requesting OpenAI TTS — voice: ${ttsVoice}, chars: ${rawText.length}`);

    try {
      const response = await fetch('/api/roadmap/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ text: rawText.slice(0, 4096), voice: ttsVoice, model: 'tts-1-hd' }),
      });

      console.log(`[Voice] Server response — status: ${response.status}, ok: ${response.ok}`);

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${response.status}`);
      }

      const audioBlob = await response.blob();
      console.log(`[Voice] ✅ Received audio blob — size: ${audioBlob.size} bytes, type: ${audioBlob.type}`);

      // Revoke previous blob URL before creating a new one
      if (ttsBlobUrlRef.current) {
        URL.revokeObjectURL(ttsBlobUrlRef.current);
      }
      const blobUrl = URL.createObjectURL(audioBlob);
      // Store blob URL persistently — export mixer needs it even after preview finishes
      ttsBlobUrlRef.current = blobUrl;

      const audio = new Audio(blobUrl);
      ttsAudioRef.current = audio;

      audio.onplay  = () => { setIsVoicePlaying(true); setTtsStatus('playing'); };
      audio.onended = () => {
        setIsVoicePlaying(false);
        setTtsStatus('idle');
        // Do NOT revoke blobUrl here — export mixer may still need it
        ttsAudioRef.current = null;
      };
      audio.onerror = () => {
        setIsVoicePlaying(false);
        setTtsStatus('error');
        setTtsError('Audio playback failed — try again.');
        ttsAudioRef.current = null;
      };

      console.log('[Voice] ▶️  Playing audio...');
      await audio.play();

    } catch (err) {
      console.warn(`[Voice] ❌ OpenAI TTS failed: ${err.message} — falling back to browser speech`);
      setTtsStatus('error');
      setTtsError(`AI voice unavailable (${err.message}) — using browser voice.`);

      // Fallback: Web Speech API
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        if (window.speechSynthesis.paused) window.speechSynthesis.resume();
        setTimeout(() => {
          const style = VOICE_STYLES.find(v => v.id === voiceStyle) || VOICE_STYLES[0];
          const cleanText = rawText.replace(/\. /g, '.  ').replace(/,\s*/g, ', ');
          const utterance = new SpeechSynthesisUtterance(cleanText);
          utterance.lang   = 'en-GB';
          utterance.rate   = style.rate;
          utterance.pitch  = style.pitch;
          utterance.volume = 1;
          const bestVoice = getBestFemaleVoice();
          if (bestVoice) utterance.voice = bestVoice;
          utterance.onstart = () => { setIsVoicePlaying(true); setTtsStatus('playing'); };
          utterance.onend   = () => { setIsVoicePlaying(false); setTtsStatus('idle'); };
          utterance.onerror = (e) => { console.warn('Speech error:', e.error); setIsVoicePlaying(false); setTtsStatus('idle'); };
          setIsVoicePlaying(true);
          window.speechSynthesis.speak(utterance);
        }, 80);
      }
    }
  }, [ttsVoice, voiceStyle, voiceGender]);

  const handleStopVoice = useCallback(() => {
    // Stop OpenAI TTS audio
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current = null;
    }
    // Stop browser speech fallback
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setIsVoicePlaying(false);
    setTtsStatus('idle');
  }, []);

  // ── Toggle hashtag ────────────────────────────────────────────────────────
  const toggleHashtag = useCallback((tag) => {
    setHashtags(prev => {
      const next = prev.includes(tag) ? prev.filter(h => h !== tag) : [...prev, tag];
      triggerAutoSave();
      return next;
    });
  }, [triggerAutoSave]);

  // ── CSS filter string for preview ─────────────────────────────────────────
  const videoFilterCss = useMemo(() => {
    const preset = FILTER_PRESETS.find(f => f.id === effectsConfig.filter);
    const base = preset?.css === 'none' ? '' : preset?.css || '';
    const adj = [
      effectsConfig.brightness ? `brightness(${1 + effectsConfig.brightness})` : '',
      effectsConfig.contrast   ? `contrast(${1 + effectsConfig.contrast})`     : '',
      effectsConfig.saturation ? `saturate(${1 + effectsConfig.saturation})`   : '',
    ].filter(Boolean).join(' ');
    return [base, adj].filter(Boolean).join(' ') || 'none';
  }, [effectsConfig]);

  // ── Preview dimensions — responsive, never overflows the 58% panel ────────
  // We let CSS do the sizing via aspect-ratio; these are just for canvas scaling
  const previewH = 420; // logical reference height (used for font scaling)
  const previewW = Math.round(previewH * activeAR.w / activeAR.h);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
    <div className="flex flex-col h-full bg-white overflow-hidden">

      {/* Hidden audio element — always in DOM so audioRef.current is never null
          regardless of which tab is active. Must live outside all conditional rendering. */}
      <audio ref={audioRef} loop preload="auto" className="hidden" />

      {/* ═══ TOP BAR ═══ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200">
        <div className="px-5 py-3 flex items-center justify-between">
          {/* Left — platform + day */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: platform.color }}>
              <i className={`${platform.icon} text-white text-sm`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold text-slate-900">{platform.name}</h2>
                <span className="text-slate-300 text-xs">|</span>
                <span className="text-xs text-slate-500">Day {day?.day}</span>
                <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-blue-100 text-blue-700">
                  <i className="fas fa-video mr-0.5 text-[8px]" />VIDEO
                </span>
              </div>
              <p className="text-[10px] text-slate-400 truncate max-w-xs mt-0.5">{day?.pillar || 'Video Content'}</p>
            </div>
          </div>

          {/* Right — auto-save + close */}
          <div className="flex items-center gap-3">
            {autoSaveStatus === 'saving' && (
              <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full">
                <i className="fas fa-circle-notch fa-spin text-blue-500 text-[8px]" />
                <span className="text-[9px] text-blue-600 font-semibold">Saving…</span>
              </div>
            )}
            {autoSaveStatus === 'saved' && (
              <div className="flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full">
                <i className="fas fa-check-circle text-emerald-500 text-[8px]" />
                <span className="text-[9px] text-emerald-600 font-semibold">Auto-saved ✓</span>
              </div>
            )}
            <button onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        {/* Section tabs */}
        <div className="px-5 flex">
          {SECTION_IDS.map((sid, i) => {
            const labels = { today: 'Today', script: 'Story', film: 'Film', edit: 'Studio' };
            const icons  = { today: 'fa-clipboard-list', script: 'fa-book-open', film: 'fa-video', edit: 'fa-wand-magic-sparkles' };
            return (
              <button key={sid} onClick={() => setActiveSection(sid)}
                className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-all flex items-center gap-1.5 ${
                  activeSection === sid ? 'text-slate-900 border-slate-900' : 'text-slate-400 border-transparent hover:text-slate-600'
                }`}>
                <i className={`fas ${icons[sid]} text-[10px]`} />
                {labels[sid]}
                {i < sectionIdx && <i className="fas fa-check-circle text-emerald-500 text-[9px]" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ CONTENT AREA ═══ */}
      <div className="flex-1 overflow-hidden">

        {/* ─── TODAY SECTION ─── */}
        {activeSection === 'today' && (
          <div className="h-full overflow-y-auto">
            {isLoadingBrief ? (
              <div className="flex flex-col items-center justify-center h-full gap-6">
                <div className="relative w-20 h-20">
                  <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="34" stroke="#e2e8f0" strokeWidth="5" fill="none" />
                    <circle cx="40" cy="40" r="34" stroke="#6366f1" strokeWidth="5" fill="none"
                      strokeDasharray="213" strokeDashoffset="53" strokeLinecap="round"
                      className="transition-all duration-700" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className={`fas ${BRIEFING_PHASES[briefPhase].icon} text-indigo-500 text-xl`} />
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-slate-700">{BRIEFING_PHASES[briefPhase].text}</p>
                  <p className="text-xs text-slate-400 mt-1">Building your video brief…</p>
                </div>
                <div className="flex gap-1">
                  {BRIEFING_PHASES.map((_, i) => (
                    <div key={i} className={`h-1.5 rounded-full transition-all ${i === briefPhase ? 'w-6 bg-indigo-500' : 'w-1.5 bg-slate-200'}`} />
                  ))}
                </div>
              </div>
            ) : briefingData ? (
              <div className="max-w-2xl mx-auto p-6 space-y-5">
                {/* Header */}
                <div className="flex items-start gap-4 p-4 bg-slate-50 border border-blue-200 rounded-2xl">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-video text-blue-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">{postTitle || day?.postTitle}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{platform.name} · Day {day?.day}</p>
                  </div>
                </div>

                {/* Task overview */}
                {briefingData.taskOverview && (
                  <div className="p-4 bg-white border border-slate-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="fas fa-bullseye text-blue-500 text-xs" />
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Today's Video Mission</span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{briefingData.taskOverview}</p>
                  </div>
                )}

                {/* Strategy rationale */}
                {briefingData.strategyRationale && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="fas fa-lightbulb text-amber-500 text-xs" />
                      <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Why This Video Now</span>
                    </div>
                    <p className="text-sm text-amber-800 leading-relaxed">{briefingData.strategyRationale}</p>
                  </div>
                )}

                {/* Platform tips */}
                {briefingData.platformTips?.length > 0 && (
                  <div className="p-4 bg-white border border-slate-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <i className={`${platform.icon} text-xs`} style={{ color: platform.color }} />
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">{platform.name} Tips</span>
                    </div>
                    <div className="space-y-2">
                      {briefingData.platformTips.map((tip, i) => (
                        <div key={i} className="flex gap-2.5">
                          <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                          <p className="text-xs text-slate-600 leading-relaxed">{tip}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Visual direction */}
                {briefingData.visualDirection && (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="fas fa-film text-slate-500 text-xs" />
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Visual Direction</span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">{briefingData.visualDirection?.reasoning || briefingData.visualDirection}</p>
                  </div>
                )}

                {/* Success metrics */}
                {briefingData.successMetrics && (
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { icon: 'fa-trophy', label: 'Primary Metric', value: briefingData.successMetrics.primaryMetric, color: '#F59E0B' },
                      { icon: 'fa-chart-bar', label: 'Benchmark',     value: briefingData.successMetrics.benchmark,     color: '#6366F1' },
                      { icon: 'fa-bolt',    label: 'Hook Target',    value: briefingData.successMetrics.hookTip,        color: '#EF4444' },
                    ].map(m => m.value && (
                      <div key={m.label} className="p-3 bg-white border border-slate-200 rounded-xl text-center">
                        <i className={`fas ${m.icon} text-base mb-1`} style={{ color: m.color }} />
                        <p className="text-[9px] text-slate-400 uppercase tracking-wide">{m.label}</p>
                        <p className="text-xs font-semibold text-slate-700 mt-0.5">{m.value}</p>
                      </div>
                    ))}
                  </div>
                )}

                <button onClick={loadBriefing}
                  className="w-full py-2.5 text-xs text-slate-500 border border-dashed border-slate-200 rounded-xl hover:bg-slate-50 transition-all flex items-center justify-center gap-2">
                  <i className="fas fa-sync-alt text-[10px]" />Regenerate Brief
                </button>

                {/* Today's Filming Plan */}
                <div className="p-4 bg-slate-900 rounded-2xl text-white">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="fas fa-film text-blue-400 text-sm" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-300">Today's Filming Plan</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-white/10 rounded-xl p-2.5">
                      <p className="text-[9px] text-slate-400 uppercase tracking-wide mb-0.5">Platform</p>
                      <p className="text-xs font-semibold">{platform.name}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-2.5">
                      <p className="text-[9px] text-slate-400 uppercase tracking-wide mb-0.5">Target Duration</p>
                      <p className="text-xs font-semibold">{targetDuration}s</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-2.5 col-span-2">
                      <p className="text-[9px] text-slate-400 uppercase tracking-wide mb-0.5">Topic</p>
                      <p className="text-xs font-semibold">{postTitle || day?.postTitle || day?.task || 'Video Content'}</p>
                    </div>
                  </div>
                  {briefingData?.platformTips?.slice(0, 2).map((tip, i) => (
                    <div key={i} className="flex gap-2 mb-1.5">
                      <span className="text-blue-400 text-xs mt-0.5 flex-shrink-0">→</span>
                      <p className="text-[10px] text-slate-300">{tip}</p>
                    </div>
                  ))}
                </div>

                {/* Generate Script CTA */}
                <button onClick={() => setActiveSection('script')}
                  className="w-full py-4 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-2xl transition-all shadow-lg shadow-blue-200/50 flex items-center justify-center gap-2">
                  <i className="fas fa-scroll" />
                  Generate My Script
                  <i className="fas fa-arrow-right text-xs opacity-70 ml-1" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-4">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center">
                  <i className="fas fa-video text-blue-400 text-2xl" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-slate-700">Building Your Video Brief…</p>
                  <p className="text-xs text-slate-400 mt-1">AI is analysing your strategy and creating a creative brief</p>
                </div>
                <button onClick={loadBriefing}
                  className="px-6 py-2.5 bg-blue-700 text-white text-sm font-semibold rounded-xl hover:bg-blue-800 transition-all flex items-center gap-2">
                  <i className="fas fa-wand-magic-sparkles" />Generate Brief
                </button>
              </div>
            )}
          </div>
        )}

        {/* ─── STORY SECTION ─── */}
        {activeSection === 'script' && (
          <div className="h-full overflow-y-auto">

            {!scriptData ? (
              /* ── STORY SETUP (before generation) ── */
              <div className="p-5 max-w-xl mx-auto space-y-5">

                {/* Hero area */}
                <div className="text-center pt-4 pb-2">
                  <div className="w-14 h-14 rounded-2xl bg-blue-700 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-blue-200">
                    <i className="fas fa-book-open text-white text-xl" />
                  </div>
                  <h2 className="text-base font-bold text-slate-900">Write Your Video Story</h2>
                  <p className="text-xs text-slate-500 mt-1">Tell AI what you want to say — it'll write your script, hook, and text overlays</p>
                </div>

                {/* Topic */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">What's this video about?</label>
                  <input value={postTitle} onChange={e => { setPostTitle(e.target.value); triggerAutoSave(); }}
                    placeholder="e.g. How our salon transformed this client's hair..."
                    className="w-full px-3.5 py-3 text-sm border border-slate-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all" />
                </div>

                {/* Tone + Duration (compact row) */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Tone</label>
                    <div className="grid grid-cols-2 gap-1">
                      {['Professional', 'Casual', 'Inspirational', 'Energetic'].map(t => (
                        <button key={t} onClick={() => { setTone(t.toLowerCase()); triggerAutoSave(); }}
                          className={`py-1.5 text-[10px] font-semibold rounded-lg border transition-all ${tone === t.toLowerCase() ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                          {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Duration Target</label>
                <div className="grid grid-cols-3 gap-1">
                  {[15, 30, 60].map(d => (
                    <button key={d} onClick={() => setTargetDuration(d)}
                      className={`py-1.5 text-[10px] font-semibold rounded-lg border transition-all ${targetDuration === d ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                      {d}s
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <button onClick={handleGenerateScript} disabled={isGenScript || !postTitle.trim()}
              className="w-full py-3.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-xl disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-200">
              {isGenScript ? (
                <><i className="fas fa-circle-notch fa-spin" /><span>Writing your story…</span></>
              ) : (
                <><i className="fas fa-wand-magic-sparkles" /><span>✨ Write My Story</span></>
              )}
            </button>

          </div>
        ) : (

          /* ── STORY CARD (after generation) ── */
          <div className="p-5 max-w-xl mx-auto space-y-4 pb-8">

            {/* ── HOOK BANNER ── */}
            <div className="relative bg-amber-500 rounded-2xl p-4 shadow-md shadow-amber-200/40">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-[10px] font-bold text-amber-100 uppercase tracking-widest mb-1">Opening Hook · First 3 seconds</p>
                  <p className="text-sm font-bold text-white leading-snug">
                    {hookOptions.length > 0
                      ? hookOptions[selectedHookIdx] || hookOptions[0]
                      : scriptData.hook || 'No hook generated'}
                  </p>
                </div>
                <button onClick={handleGenerateHooks} disabled={isGenHooks}
                  className="flex-shrink-0 p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-all disabled:opacity-50" title="Regenerate hooks">
                  {isGenHooks
                    ? <i className="fas fa-circle-notch fa-spin text-white text-xs" />
                    : <i className="fas fa-sync-alt text-white text-xs" />}
                </button>
              </div>
              {hookOptions.length > 1 && (
                <div className="flex gap-1.5 mt-2.5 flex-wrap">
                  {hookOptions.map((_, i) => (
                    <button key={i} onClick={() => setSelectedHookIdx(i)}
                      className={`w-5 h-1.5 rounded-full transition-all ${i === selectedHookIdx ? 'bg-white' : 'bg-white/40'}`} />
                  ))}
                  <span className="text-[9px] text-amber-100 ml-1 self-center">Tap dots to switch hook</span>
                </div>
              )}
            </div>

            {/* ── SCENE PILLS ── */}
            {scriptData.scenes?.length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                  {scriptData.scenes.length} Scenes · ~{targetDuration}s
                </p>
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                  {scriptData.scenes.map((scene, i) => {
                    const tc = SCENE_TYPE_COLORS[scene.type] || { bg: '#F1F5F9', text: '#475569', label: scene.type?.toUpperCase() };
                    return (
                      <div key={i} className="flex-shrink-0 flex flex-col items-center gap-1 min-w-[64px]">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg" style={{ backgroundColor: tc.bg }}>
                          {i === 0 ? '🎬' : i === scriptData.scenes.length - 1 ? '🎯' : '🎥'}
                        </div>
                        <span className="text-[9px] font-semibold text-center leading-tight" style={{ color: tc.text }}>
                          {tc.label}
                        </span>
                        <span className="text-[8px] text-slate-400 font-mono">{scene.duration}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── VOICEOVER TELEPROMPTER ── */}
            <div className="bg-slate-900 rounded-2xl overflow-hidden shadow-lg">
              <div className="px-4 py-2.5 flex items-center justify-between border-b border-slate-700">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <div className="w-2 h-2 rounded-full bg-green-400" />
                  <span className="text-[10px] text-slate-400 ml-2 font-mono">voiceover_script.txt</span>
                </div>
                <span className="text-[9px] text-slate-500">{scriptData.voiceover?.length || 0} chars</span>
              </div>
              <textarea
                value={scriptData.voiceover || ''}
                onChange={e => { setScriptData(prev => ({ ...prev, voiceover: e.target.value })); triggerAutoSave(); }}
                rows={6}
                className="w-full bg-transparent text-green-300 text-xs px-4 py-3.5 leading-relaxed font-mono resize-none focus:outline-none placeholder-slate-600"
                placeholder="Your word-for-word voiceover will appear here…"
              />
            </div>

            {/* ── B-ROLL SHOTS ── */}
            {scriptData.broll?.length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">Film These Shots</p>
                <div className="flex flex-wrap gap-1.5">
                  {scriptData.broll.map((b, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded-full">
                      <i className="fas fa-camera text-[8px]" />{b}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* ── MUSIC SUGGESTION ── */}
            {scriptData.musicMood && (
              <div className="flex items-center gap-3 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-xl">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-music text-indigo-500 text-xs" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wide">Suggested Music Vibe</p>
                  <p className="text-xs text-indigo-800 font-medium truncate">{scriptData.musicMood}</p>
                </div>
              </div>
            )}

            {/* ── CTA ── */}
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
              <i className="fas fa-hand-pointer text-blue-500 text-sm flex-shrink-0" />
              <input value={ctaText} onChange={e => { setCtaText(e.target.value); triggerAutoSave(); }}
                placeholder="End screen CTA — e.g. Book via link in bio"
                className="flex-1 bg-transparent text-xs text-slate-700 placeholder-slate-400 focus:outline-none" />
            </div>

            {/* ── APPROVE CTA ── */}
            <div className="pt-1 pb-6 space-y-2.5">
              <button
                onClick={() => {
                  setScriptApproved(true);
                  triggerAutoSave();
                  setActiveSection('film');
                }}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-2xl transition-all/50 flex items-center justify-center gap-2">
                <i className="fas fa-check-circle" />
                Looks Good — Start Filming
                <i className="fas fa-arrow-right text-xs opacity-70 ml-1" />
              </button>
              <button
                onClick={() => { setScriptData(null); setScriptApproved(false); }}
                className="w-full py-2 text-xs text-slate-400 hover:text-slate-600 transition-colors">
                ← Rewrite from scratch
              </button>
            </div>

          </div>
        )}
        </div>
        )}

        {/* ─── FILM SECTION ─── */}
        {activeSection === 'film' && (
          <div className="h-full overflow-y-auto p-5 max-w-2xl mx-auto space-y-5">

            {/* Script cheat sheet (if approved) */}
            {scriptApproved && scriptData && (
              <div className="border border-emerald-200 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setCheatSheetOpen(p => !p)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left bg-emerald-50 hover:bg-emerald-100 transition-all">
                  <i className="fas fa-check-circle text-emerald-500 text-sm" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-emerald-800">Script Approved — Your Filming Guide</p>
                    <p className="text-[10px] text-emerald-600">Tap to {cheatSheetOpen ? 'collapse' : 'expand'} while filming</p>
                  </div>
                  <i className={`fas fa-chevron-${cheatSheetOpen ? 'up' : 'down'} text-emerald-500 text-xs`} />
                </button>
                {cheatSheetOpen && (
                  <div className="divide-y divide-emerald-100">
                    {(scriptData.scenes || []).map((scene, i) => {
                      const tc = SCENE_TYPE_COLORS[scene.type] || { bg: '#F1F5F9', text: '#475569', label: scene.type?.toUpperCase() };
                      return (
                        <div key={i} className="px-4 py-3 bg-white space-y-1.5">
                          <div className="flex items-center gap-2">
                            <span className="px-1.5 py-0.5 text-[8px] font-bold rounded-full" style={{ backgroundColor: tc.bg, color: tc.text }}>{tc.label}</span>
                            <span className="text-[9px] text-slate-400 font-mono">{scene.duration}</span>
                          </div>
                          {scene.visual && (
                            <div className="flex gap-2">
                              <span className="text-[9px] font-bold text-slate-400 uppercase w-10 flex-shrink-0 mt-0.5">Film</span>
                              <p className="text-[11px] text-slate-700">{scene.visual}</p>
                            </div>
                          )}
                          {scene.voiceover && (
                            <div className="flex gap-2">
                              <span className="text-[9px] font-bold text-slate-400 uppercase w-10 flex-shrink-0 mt-0.5">Say</span>
                              <p className="text-[11px] text-slate-600 italic">{scene.voiceover}</p>
                            </div>
                          )}
                          {scene.textOverlay && (
                            <div className="flex gap-2">
                              <span className="text-[9px] font-bold text-indigo-400 uppercase w-10 flex-shrink-0 mt-0.5">Text</span>
                              <p className="text-[11px] text-indigo-600 font-medium">{scene.textOverlay}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Smart clip guidance */}
            {(() => {
              const MAX_CLIPS = 5;
              const MAX_SECS  = 60;
              const scenes    = scriptData?.scenes || [];
              const totalSecs = totalClipDuration;
              const overTime  = totalSecs > MAX_SECS;
              const overCount = clips.length > MAX_CLIPS;

              // Per-scene timing budget (distribute MAX_SECS evenly if no scene durations)
              const sceneBudgets = scenes.map(s => {
                const match = String(s.duration || '').match(/(\d+)-(\d+)/);
                return match ? parseInt(match[2]) - parseInt(match[1]) : Math.floor(MAX_SECS / Math.max(scenes.length, 1));
              });

              return (
                <>
                  {/* Rules banner */}
                  <div className="flex gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-2xl">
                    <div className="flex flex-col gap-1.5 flex-1">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-video text-indigo-500 text-xs" />
                        <p className="text-xs font-bold text-indigo-800">Clip Checklist</p>
                      </div>
                      <div className="flex gap-4">
                        <div className={`flex items-center gap-1.5 ${overCount ? 'text-red-600' : 'text-indigo-600'}`}>
                          <i className={`fas ${overCount ? 'fa-exclamation-circle' : 'fa-check-circle'} text-[10px]`} />
                          <span className="text-[10px] font-semibold">{clips.length}/{MAX_CLIPS} clips{overCount ? ' — too many!' : ''}</span>
                        </div>
                        <div className={`flex items-center gap-1.5 ${overTime ? 'text-red-600' : 'text-indigo-600'}`}>
                          <i className={`fas ${overTime ? 'fa-exclamation-circle' : 'fa-check-circle'} text-[10px]`} />
                          <span className="text-[10px] font-semibold">{fmtTime(totalSecs)}/{MAX_SECS}s total{overTime ? ' — too long!' : ''}</span>
                        </div>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="w-24 flex flex-col justify-center gap-1">
                      <div className="h-1.5 bg-indigo-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min((totalSecs / MAX_SECS) * 100, 100)}%`, background: overTime ? '#ef4444' : '#6366f1' }} />
                      </div>
                      <p className="text-[9px] text-indigo-500 text-right">{Math.round((totalSecs / MAX_SECS) * 100)}% of limit</p>
                    </div>
                  </div>

                  {/* Scene shot cards (one per scene in script) */}
                  {scenes.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">What to Film — Scene by Scene</p>
                      {scenes.map((scene, i) => {
                        const uploaded = clips[i];
                        const tc = SCENE_TYPE_COLORS[scene.type] || { bg: '#F1F5F9', text: '#475569', label: scene.type?.toUpperCase() || 'SCENE' };
                        return (
                          <div key={i} className={`rounded-xl border px-3 py-2.5 flex gap-3 transition-all ${uploaded ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                            <div className="flex flex-col items-center gap-1 flex-shrink-0">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold ${uploaded ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                                {uploaded ? <i className="fas fa-check text-[8px]" /> : i + 1}
                              </div>
                              <span className="text-[8px] font-mono text-slate-400">{sceneBudgets[i]}s</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="px-1.5 py-0.5 rounded text-[8px] font-bold" style={{ backgroundColor: tc.bg, color: tc.text }}>{tc.label}</span>
                                <span className="text-[9px] text-slate-400">{scene.duration}</span>
                              </div>
                              {scene.visual && <p className="text-[11px] text-slate-700 leading-tight">📷 {scene.visual}</p>}
                              {scene.voiceover && <p className="text-[10px] text-slate-500 italic mt-0.5 truncate">🎙 "{scene.voiceover}"</p>}
                            </div>
                            {uploaded && (
                              <div className="flex-shrink-0 flex flex-col items-end gap-1 justify-center">
                                <span className="text-[9px] text-emerald-600 font-semibold">{(uploaded.trimEnd - uploaded.trimStart).toFixed(1)}s</span>
                                <button onClick={() => {
                                  if (uploaded.blobUrl) URL.revokeObjectURL(uploaded.blobUrl);
                                  setClips(prev => prev.filter(c => c.id !== uploaded.id));
                                }} className="text-red-400 hover:text-red-600">
                                  <i className="fas fa-trash text-[8px]" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Extra clips beyond scenes */}
                      {clips.slice(scenes.length).map((clip, ei) => (
                        <div key={clip.id} className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-amber-400 text-white flex items-center justify-center text-[8px] font-bold flex-shrink-0">{scenes.length + ei + 1}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-semibold text-amber-800 truncate">{clip.name}</p>
                            <p className="text-[9px] text-amber-600">{(clip.trimEnd - clip.trimStart).toFixed(1)}s · Extra clip</p>
                          </div>
                          <button onClick={() => {
                            if (clip.blobUrl) URL.revokeObjectURL(clip.blobUrl);
                            setClips(prev => prev.filter(c => c.id !== clip.id));
                          }} className="text-red-400 hover:text-red-600 flex-shrink-0">
                            <i className="fas fa-trash text-[9px]" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload zone */}
                  <div>
                    {scenes.length === 0 && (
                      <p className="text-xs text-slate-500 mb-2">Film your video following the script guide, then upload it here. Max <strong>5 clips · 60s total</strong>.</p>
                    )}
                    <label className={`flex items-center justify-center gap-3 py-5 border-2 border-dashed rounded-2xl cursor-pointer transition-all group ${clips.length >= MAX_CLIPS ? 'border-red-200 bg-red-50 opacity-60 pointer-events-none' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                      <input type="file" accept="video/mp4,video/quicktime,video/webm,video/x-msvideo" multiple className="hidden" onChange={handleClipUpload} disabled={clips.length >= MAX_CLIPS} />
                      <i className={`fas fa-cloud-upload-alt text-2xl transition-all ${clips.length >= MAX_CLIPS ? 'text-red-300' : 'text-slate-300 group-hover:text-blue-400'}`} />
                      <div>
                        <p className={`text-sm font-semibold ${clips.length >= MAX_CLIPS ? 'text-red-400' : 'text-slate-600 group-hover:text-blue-700'}`}>
                          {clips.length >= MAX_CLIPS ? 'Max 5 clips reached — remove one to add more' : scenes.length > 0 ? `Upload Clip ${clips.length + 1} of ${Math.min(scenes.length, MAX_CLIPS)}` : 'Tap to Upload Footage'}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">MP4, MOV, WebM · Up to 500MB each</p>
                      </div>
                    </label>
                  </div>

                  {/* Edit with AI CTA */}
                  {clips.length > 0 ? (
                    <div className="pb-6 space-y-3">
                      {(overTime || overCount) && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                          <p className="text-[11px] text-red-700 font-medium flex items-start gap-2">
                            <i className="fas fa-exclamation-triangle mt-0.5 flex-shrink-0" />
                            {overCount ? `Remove clips to stay under ${MAX_CLIPS} clips. ` : ''}{overTime ? `Trim clips to stay under ${MAX_SECS}s total. ` : ''}AI works best within these limits.
                          </p>
                        </div>
                      )}
                      <div className="p-3.5 bg-blue-50 border border-blue-200 rounded-xl">
                        <p className="text-xs text-blue-700 font-medium flex items-start gap-2">
                          <i className="fas fa-wand-magic-sparkles text-blue-500 mt-0.5 flex-shrink-0" />
                          {clips.length === 1
                            ? 'AI will stitch and edit your clip — applying text overlays, colour grade, music and captions perfectly timed.'
                            : `AI will stitch your ${clips.length} clips together and apply text overlays, colour grade, music and captions perfectly timed.`}
                        </p>
                      </div>
                      <button
                        onClick={() => { editTriggeredRef.current = false; setActiveSection('edit'); }}
                        className="w-full py-4 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold rounded-2xl transition-all shadow-lg shadow-blue-200/50 flex items-center justify-center gap-2">
                        <i className="fas fa-wand-magic-sparkles" />
                        {clips.length > 1 ? `Stitch & Edit ${clips.length} Clips with AI` : 'Edit My Video with AI'}
                        <i className="fas fa-arrow-right text-xs opacity-70 ml-1" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-400">
                      <i className="fas fa-film text-4xl mb-2 text-slate-200" />
                      <p className="text-sm font-medium">Upload your footage above to continue</p>
                      <p className="text-xs mt-1">Once uploaded, AI will stitch and edit it automatically</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* ─── EDIT SECTION ─── */}
        {activeSection === 'edit' && (
          <div className="flex flex-col h-full relative">

            {/* ── FULL-SCREEN AUTO-EDIT LOADING OVERLAY ── */}
            {isAutoEditing && (
              <div className="absolute inset-0 z-50 bg-slate-900 flex flex-col items-center justify-center gap-6">
                <div className="relative w-24 h-24">
                  <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                    <circle cx="48" cy="48" r="40" stroke="rgba(255,255,255,0.1)" strokeWidth="6" fill="none" />
                    <circle cx="48" cy="48" r="40" stroke="rgba(167,139,250,1)" strokeWidth="6" fill="none"
                      strokeDasharray="251" strokeLinecap="round"
                      style={{ strokeDashoffset: 251 - ((autoEditStep + 1) / AUTO_EDIT_STEPS.length) * 251, transition: 'stroke-dashoffset 0.7s ease' }} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className={`fas ${AUTO_EDIT_STEPS[autoEditStep]?.icon || 'fa-spinner fa-spin'} text-blue-300 text-2xl`} />
                  </div>
                </div>
                <div className="text-center px-8">
                  <p className="text-white font-bold text-lg mb-1">AI is Editing Your Video</p>
                  <p className="text-blue-300 text-sm">{AUTO_EDIT_STEPS[autoEditStep]?.text}</p>
                </div>
                <div className="flex gap-1.5">
                  {AUTO_EDIT_STEPS.map((_, i) => (
                    <div key={i} className={`h-1.5 rounded-full transition-all ${i <= autoEditStep ? 'bg-blue-500 w-4' : 'bg-white/20 w-1.5'}`} />
                  ))}
                </div>
              </div>
            )}

            {/* ── AI AUTO-EDIT BANNER ── */}
            {!autoEditDone && !isAutoEditing && enabledClips.length > 0 && (
              <div className="flex-shrink-0 mx-4 mt-3 mb-1 rounded-2xl overflow-hidden border border-blue-200 bg-slate-50">
                <div className="flex items-center gap-4 px-4 py-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-700 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-200">
                    <i className="fas fa-wand-magic-sparkles text-white text-sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-bold text-slate-800 leading-tight">Auto-Edit with AI</p>
                    <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                      AI will analyse your content and automatically apply text overlays, colour grade, music mood, captions and effects — perfectly timed to your video.
                    </p>
                  </div>
                  <button
                    onClick={handleAutoEdit}
                    className="flex-shrink-0 px-4 py-2 bg-blue-700 text-white text-[11px] font-bold rounded-xl  transition-all shadow-md shadow-blue-200 flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <i className="fas fa-wand-magic-sparkles text-[10px]" />
                    Apply Auto-Edit
                  </button>
                </div>
              </div>
            )}

            {/* ── AUTO-EDIT LOADING (inline banner – hidden while full-screen overlay is showing) ── */}

            {/* ── AUTO-EDIT DONE ── */}
            {autoEditDone && (
              <div className="flex-shrink-0 mx-4 mt-2 mb-1">
                {autoEditCollapsed ? (
                  /* Collapsed — single slim bar */
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-emerald-200 bg-emerald-50 cursor-pointer" onClick={() => setAutoEditCollapsed(false)}>
                    <i className="fas fa-check-circle text-emerald-500 text-[10px]" />
                    <span className="text-[10px] font-bold text-emerald-700 flex-1">Auto-Edit Applied</span>
                    <div className="flex gap-1">
                      <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-600 text-[8px] font-bold rounded-full">{textOverlays.length} overlays</span>
                      <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[8px] font-bold rounded-full capitalize">{effectsConfig.filter}</span>
                      <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-[8px] font-bold rounded-full capitalize">{audioConfig.mood}</span>
                    </div>
                    <i className="fas fa-chevron-down text-emerald-400 text-[8px]" />
                  </div>
                ) : (
                  /* Expanded */
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-check-circle text-emerald-500 text-[11px]" />
                      <span className="text-[11px] font-bold text-emerald-800 flex-1">Auto-Edit Applied!</span>
                      <div className="flex gap-1">
                        <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[8px] font-bold rounded-full">{textOverlays.length} overlays</span>
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[8px] font-bold rounded-full capitalize">{effectsConfig.filter || 'natural'} filter</span>
                        <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[8px] font-bold rounded-full capitalize">{audioConfig.mood} music</span>
                      </div>
                      <button onClick={() => { setAutoEditDone(false); setAutoEditCollapsed(false); }} className="text-[9px] font-semibold text-emerald-700 border border-emerald-300 rounded-lg px-2 py-0.5 hover:bg-emerald-100 whitespace-nowrap">Re-run</button>
                      <button onClick={() => setAutoEditCollapsed(true)} className="w-5 h-5 flex items-center justify-center text-emerald-400 hover:text-emerald-600"><i className="fas fa-chevron-up text-[8px]" /></button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── AUTO-EDIT ERROR ── */}
            {autoEditError && !isAutoEditing && (
              <div className="flex-shrink-0 mx-4 mt-3 mb-1 rounded-2xl border border-red-200 bg-red-50 px-4 py-2.5 flex items-center gap-2">
                <i className="fas fa-exclamation-circle text-red-500 text-xs flex-shrink-0" />
                <p className="text-[10px] text-red-700 flex-1">{autoEditError}</p>
                <button onClick={() => setAutoEditError('')} className="text-red-400 hover:text-red-600">
                  <i className="fas fa-times text-[9px]" />
                </button>
              </div>
            )}

            {/* ── OUTPUT MODE TOGGLE ── */}
            <div className="flex-shrink-0 mx-4 mt-2 mb-1 flex items-center gap-2">
              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wide flex-shrink-0">Output:</span>
              <div className="flex rounded-xl overflow-hidden border border-slate-200 flex-shrink-0">
                <button
                  onClick={() => { setOutputMode('voice+music'); triggerAutoSave(); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold transition-all ${outputMode === 'voice+music' ? 'bg-blue-700 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                  <i className="fas fa-microphone text-[8px]" />Voice + Music
                </button>
                <button
                  onClick={() => { setOutputMode('music-only'); triggerAutoSave(); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold transition-all border-l border-slate-200 ${outputMode === 'music-only' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                  <i className="fas fa-music text-[8px]" />Music Only
                </button>
              </div>
              {outputMode === 'music-only' && scriptDataRef.current && (
                <button
                  onClick={generateTextOverlaysFromScript}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all whitespace-nowrap">
                  <i className="fas fa-magic text-[8px]" />Auto-fill text from script
                </button>
              )}
              {outputMode === 'music-only' && (
                <span className="text-[9px] text-slate-400 leading-tight">Text overlays tell your story — make them bold and timed well.</span>
              )}
            </div>

            {/* ── NO CLIPS YET — prompt upload ── */}
            {enabledClips.length === 0 && !isAutoEditing && (
              <div className="flex-shrink-0 mx-4 mt-3 mb-1 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-center">
                <i className="fas fa-film text-slate-300 text-2xl mb-1.5" />
                <p className="text-[11px] font-semibold text-slate-500">No footage uploaded yet</p>
                <p className="text-[9px] text-slate-400 mt-0.5 mb-2">Go to the Film step to upload your video clips</p>
                <button onClick={() => setActiveSection('film')}
                  className="px-4 py-1.5 text-[10px] font-semibold text-blue-700 bg-blue-100 border border-blue-200 rounded-lg hover:bg-blue-200 transition-all">
                  <i className="fas fa-arrow-left text-[8px] mr-1" />Go to Film
                </button>
              </div>
            )}

            {/* ── EDITOR PANELS ── */}
            <div className="flex flex-1 min-h-0 overflow-hidden">

            {/* LEFT: Preview + Timeline */}
            <div className="flex flex-col bg-slate-900 overflow-hidden" style={{ width: panelOpen ? '60%' : 'calc(100% - 36px)', minWidth: 0 }}>

              {/* Video Preview — uses CSS aspect-ratio so it fills available space perfectly */}
              <div className="flex-1 flex items-center justify-center p-2 relative overflow-hidden" style={{ minHeight: 0 }}>
                <div className="relative rounded-xl overflow-hidden shadow-2xl border border-slate-700 bg-black"
                  style={{
                    /* CSS aspect-ratio drives the size — never overflows horizontally */
                    aspectRatio: `${activeAR.w} / ${activeAR.h}`,
                    maxHeight: '100%',
                    maxWidth: '100%',
                    height: activeAR.w > activeAR.h ? 'auto' : '100%',
                    width:  activeAR.w > activeAR.h ? '100%' : 'auto',
                  }}>

                  {enabledClips.length > 0 ? (
                    <>
                      <video ref={videoRef}
                        src={enabledClips[0]?.blobUrl}
                        onLoadedMetadata={handleVideoLoaded}
                        onPlay={() => { playPendingRef.current = false; setIsPlaying(true); }}
                        onPause={() => setIsPlaying(false)}
                        onEnded={() => { playPendingRef.current = false; setIsPlaying(false); stopMusic(); }}
                        onCanPlay={() => {
                          // Set volume via JS (muted attr stays false so audio works)
                          if (videoRef.current) videoRef.current.volume = (audioConfig.videoVolume ?? 100) / 100;
                        }}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', filter: videoFilterCss }}
                        muted={false}
                        playsInline
                        preload="auto"
                        className="block"
                      />
                      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
                      {/* ── COVER IMAGE OVERLAY — shown for first 3 seconds ── */}
                      {coverImageUrl && currentTime < 3 && !isPlaying && (
                        <div className="absolute inset-0 z-[5] pointer-events-none overflow-hidden">
                          <img src={coverImageUrl} alt="" className="w-full h-full object-cover" style={{ opacity: currentTime > 2.5 ? Math.max(0, 1 - (currentTime - 2.5) / 0.5) : 1 }} />
                        </div>
                      )}
                      {/* ── INLINE EDITABLE TEXT OVERLAYS — professional animated ── */}
                      {!isPlaying && textOverlays.map(ov => {
                        const samePos = textOverlays.filter(o => o.position === ov.position);
                        const posIdx  = samePos.findIndex(o => o.id === ov.id);
                        const baseY   = ov.position === 'top' ? 6 : ov.position === 'center' ? 42 : 70;
                        const yPct    = baseY + posIdx * 12;
                        // Responsive font: proportional to container, hard-capped so it never overflows
                        const scaledSize = `clamp(11px, ${((ov.fontSize || 48) / 1080) * 56}vw, ${Math.min(ov.fontSize || 48, 34)}px)`;
                        const animClass  = ANIM_CLASS[ov.animation] || '';
                        // Background styles
                        const bgStyle = ov.background === 'box'
                          ? { backgroundColor: 'rgba(0,0,0,0.62)', borderRadius: '6px' }
                          : ov.background === 'bar'
                          ? { backgroundColor: 'rgba(0,0,0,0.52)', borderRadius: 0, display: 'block', width: '100%' }
                          : {};
                        return (
                          <div
                            key={ov.id}
                            className="absolute left-0 right-0 px-2 select-none text-center"
                            style={{ top: `${yPct}%`, zIndex: 20 + posIdx }}
                          >
                            {editingOverlayId === ov.id ? (
                              <input
                                value={ov.text}
                                onChange={e => updateOverlay(ov.id, { text: e.target.value })}
                                onKeyDown={e => e.key === 'Enter' && setEditingOverlayId(null)}
                                className="bg-black/75 outline-none border-2 border-blue-400 rounded-lg px-2 py-1 text-center text-white w-full"
                                style={{ fontSize: scaledSize, fontWeight: ov.bold ? '800' : '600', letterSpacing: '0.01em' }}
                              />
                            ) : (
                              <span
                                key={`${ov.id}_${ov.text}`} // key change re-triggers CSS animation
                                onClick={() => setEditingOverlayId(ov.id)}
                                title="Click to edit"
                                className={`inline-block px-3 py-1 cursor-pointer ring-2 ring-transparent hover:ring-blue-400 transition-colors max-w-full break-words ${animClass}`}
                                style={{
                                  fontSize: scaledSize,
                                  fontWeight:     ov.bold ? '800' : '600',
                                  color:          ov.color || '#fff',
                                  fontFamily:     ov.fontFamily || 'Poppins, Arial, sans-serif',
                                  letterSpacing:  '0.02em',
                                  lineHeight:     '1.2',
                                  textShadow:     '0 2px 12px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,1)',
                                  wordBreak:      'break-word',
                                  overflowWrap:   'break-word',
                                  WebkitTextStroke: ov.bold ? '0.5px rgba(0,0,0,0.4)' : 'none',
                                  ...bgStyle,
                                }}
                              >
                                {ov.text}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                      <i className="fas fa-play-circle text-slate-600 text-4xl" />
                      <p className="text-xs text-slate-500">Upload clips in the Clips tab</p>
                    </div>
                  )}

                  {/* Hook overlay — only shown when no hook in textOverlays (avoids duplication) */}
                  {(hookOptions[selectedHookIdx] || scriptData?.hook) && currentTime < 3
                    && !textOverlays.some(o => o.id?.startsWith('ov_hook')) && (
                    <div className="absolute top-0 left-0 right-0 p-3 bg-black/60">
                      <p className="text-white text-sm font-black text-center drop-shadow-lg leading-tight">
                        {hookOptions[selectedHookIdx] || scriptData?.hook}
                      </p>
                    </div>
                  )}

                  {/* CTA overlay — only shown when no CTA in textOverlays (avoids duplication) */}
                  {ctaText && totalDuration && currentTime >= totalDuration - 4
                    && !textOverlays.some(o => o.id?.startsWith('ov_cta')) && (
                    <div className="absolute bottom-0 left-0 right-0 p-3" style={{ backgroundColor: brandColors.primary + 'DD' }}>
                      <p className="text-white text-xs font-bold text-center">{ctaText}</p>
                    </div>
                  )}

                  {/* Aspect ratio badge */}
                  <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-black/50 rounded-full text-[9px] text-white font-bold">
                    {aspectRatio}
                  </div>

                  {/* ── PREVIEW BUTTON ── */}
                  {enabledClips.length > 0 && (
                    <button
                      onClick={() => {
                        const vid = videoRef.current;
                        if (vid && !vid.paused) vid.pause();
                        stopMusic();
                        setMusicPreviewPlaying(false);
                        // Capture blob URL now — portal condition evaluated on next render
                        const blobUrl = enabledClips[0]?.blobUrl || '';
                        if (!blobUrl) return;
                        setPreviewBlobUrl(blobUrl);
                        setShowPreview(true);
                        setPreviewPlaying(false);
                      }}
                      className="absolute top-2 left-2 z-30 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-white text-[10px] font-bold shadow-xl transition-all hover:scale-105 active:scale-95"
                      style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', boxShadow: '0 4px 15px rgba(124,58,237,0.5)' }}
                      title="Preview on phone"
                    >
                      <i className="fas fa-mobile-alt text-[9px]" />
                      Preview
                    </button>
                  )}
                </div>

                {/* Aspect ratio selector */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 bg-slate-800/90 rounded-lg p-1">
                  {ASPECT_RATIOS.map(r => (
                    <button key={r.id} onClick={() => { setAspectRatio(r.id); triggerAutoSave(); }}
                      className={`px-2 py-1 text-[9px] font-bold rounded transition-all ${aspectRatio === r.id ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}>
                      {r.id}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── TRANSPORT BAR ── */}
              <div className="flex-shrink-0 bg-slate-900 border-t border-slate-700 px-3 py-2 flex items-center gap-2">
                <button onClick={togglePlay} disabled={!enabledClips.length}
                  className="w-9 h-9 bg-white rounded-full flex items-center justify-center text-slate-900 hover:bg-slate-100 transition-all disabled:opacity-40 flex-shrink-0 shadow-md">
                  <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'} text-xs`} />
                </button>
                <span className="text-[11px] text-white font-mono w-24 flex-shrink-0 tabular-nums">
                  {fmtTime(currentTime)} / {fmtTime(totalDuration || totalClipDuration)}
                </span>
                <div className="flex-1 h-2 bg-slate-700 rounded-full cursor-pointer relative group"
                  onClick={e => {
                    const r = e.currentTarget.getBoundingClientRect();
                    handleSeek((e.clientX - r.left) / r.width);
                  }}>
                  <div className="h-full bg-blue-500 rounded-full transition-none"
                    style={{ width: `${totalDuration ? (currentTime / totalDuration) * 100 : 0}%` }} />
                  <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md border-2 border-blue-500 pointer-events-none"
                    style={{ left: `calc(${totalDuration ? (currentTime / totalDuration) * 100 : 0}% - 6px)` }} />
                </div>
                <div className="flex gap-0.5 flex-shrink-0">
                  {[0.5, 1, 1.5, 2].map(s => (
                    <button key={s} onClick={() => { setEffectsConfig(p => ({ ...p, speed: s })); if (videoRef.current) videoRef.current.playbackRate = s; }}
                      className={`px-1.5 py-0.5 text-[9px] rounded font-bold transition-all ${effectsConfig.speed === s ? 'bg-white text-slate-900' : 'text-slate-600 hover:text-white'}`}>
                      {s}x
                    </button>
                  ))}
                </div>
              </div>

              {/* ── MULTI-TRACK TIMELINE ── */}
              <div className="flex-shrink-0 bg-[#0d1117] border-t border-slate-700 select-none" style={{ minHeight: 160 }}>
                {/* Time ruler */}
                <div className="flex border-b border-slate-800 ml-16">
                  {Array.from({ length: 10 }).map((_, i) => {
                    const t = ((totalDuration || totalClipDuration || 30) * i / 9);
                    return (
                      <div key={i} className="flex-1 text-center border-l border-slate-800 first:border-l-0 py-0.5">
                        <span className="text-[8px] text-slate-600 font-mono">{fmtTime(t)}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Track container — clickable for seeking */}
                <div className="relative"
                  onClick={e => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const trackLeft = 64; // label width
                    const x = e.clientX - rect.left - trackLeft;
                    const trackW = rect.width - trackLeft;
                    const pct = Math.max(0, Math.min(1, x / trackW));
                    handleSeek(pct);
                  }}>

                  {/* VIDEO TRACK */}
                  <div className="flex items-center border-b border-slate-800/60">
                    <div className="w-16 flex-shrink-0 px-1.5 py-2 flex items-center gap-1">
                      <i className="fas fa-film text-[8px] text-slate-500" />
                      <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wide">Video</span>
                    </div>
                    <div className="flex-1 h-12 relative overflow-hidden flex gap-px items-center py-1">
                      {enabledClips.length > 0 ? enabledClips.map((clip, ci) => {
                        const dur = totalDuration || totalClipDuration || 30;
                        const w = ((clip.trimEnd - clip.trimStart) / dur) * 100;
                        return (
                          <div key={clip.id} onClick={e => { e.stopPropagation(); setSelectedClipId(clip.id); }}
                            className={`relative flex-shrink-0 h-10 rounded overflow-hidden cursor-pointer border transition-all ${selectedClipId === clip.id ? 'border-blue-400' : 'border-slate-600'}`}
                            style={{ width: `${Math.max(2, w)}%` }}>
                            <div className="absolute inset-0 bg-slate-700 flex items-center px-1.5 gap-1">
                              <div className="absolute inset-0 flex items-center px-1 gap-px opacity-30">
                                {Array.from({ length: 16 }).map((_, i) => (
                                  <div key={i} className="flex-1 bg-white rounded-sm" style={{ height: `${15 + Math.abs(Math.sin(i * 1.7 + ci * 2.3)) * 75}%` }} />
                                ))}
                              </div>
                              <i className="fas fa-film text-[7px] text-white/60 relative z-10 flex-shrink-0" />
                              <span className="text-[8px] text-white font-semibold truncate relative z-10">{clip.label || clip.name?.slice(0, 10)}</span>
                              <span className="text-[7px] text-slate-400 ml-auto flex-shrink-0 relative z-10">{(clip.trimEnd - clip.trimStart).toFixed(1)}s</span>
                            </div>
                          </div>
                        );
                      }) : (
                        <div className="flex-1 h-10 border border-dashed border-slate-700 rounded flex items-center justify-center">
                          <span className="text-[8px] text-slate-700">Upload clips →</span>
                        </div>
                      )}
                      {enabledClips.length > 0 && (
                        <div className="flex-shrink-0 h-10 rounded flex items-center justify-center px-2 border border-dashed"
                          style={{ borderColor: brandColors.primary + '60', backgroundColor: brandColors.primary + '18', width: '6%' }}>
                          <span className="text-[7px] font-bold" style={{ color: brandColors.primary }}>CTA</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* TEXT TRACK */}
                  <div className="flex items-center border-b border-slate-800/60">
                    <div className="w-16 flex-shrink-0 px-1.5 py-1.5 flex items-center gap-1">
                      <i className="fas fa-font text-[8px] text-indigo-400" />
                      <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wide">Text</span>
                    </div>
                    <div className="flex-1 h-8 relative" style={{ minWidth: 0 }}>
                      {textOverlays.length === 0 && (
                        <div className="absolute inset-0 flex items-center px-2">
                          <span className="text-[8px] text-slate-700 italic">Add text overlay →</span>
                        </div>
                      )}
                      {textOverlays.map(ov => {
                        const dur = totalDuration || totalClipDuration || 30;
                        const leftPct = (ov.startTime / dur) * 100;
                        const widthPct = Math.max(2, (ov.duration / dur) * 100);
                        const isActive = currentTime >= ov.startTime && currentTime <= ov.startTime + ov.duration;
                        return (
                          <div key={ov.id}
                            onClick={e => { e.stopPropagation(); setEditingOverlayId(ov.id); setControlTab('text'); }}
                            className={`absolute top-1 bottom-1 rounded flex items-center px-1.5 cursor-pointer border transition-all ${editingOverlayId === ov.id ? 'border-indigo-400 bg-indigo-500/70' : isActive ? 'border-indigo-500/80 bg-indigo-500/50' : 'border-indigo-700/60 bg-indigo-900/60 hover:bg-indigo-800/60'}`}
                            style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                            title={`${ov.text} · ${fmtTime(ov.startTime)} – ${fmtTime(ov.startTime + ov.duration)}`}>
                            <span className="text-[7px] text-white font-semibold truncate">{ov.text}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* MUSIC TRACK */}
                  <div className="flex items-center border-b border-slate-800/60">
                    <div className="w-16 flex-shrink-0 px-1.5 py-1.5 flex items-center gap-1">
                      <i className="fas fa-music text-[8px] text-emerald-400" />
                      <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wide">Music</span>
                    </div>
                    <div className="flex-1 h-7 relative overflow-hidden">
                      {audioConfig.mood !== 'none' ? (
                        <div className="absolute inset-y-1 left-0 right-0 rounded flex items-center px-2 gap-px overflow-hidden"
                          style={{ backgroundColor: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.25)' }}>
                          {Array.from({ length: 60 }).map((_, i) => (
                            <div key={i} className="flex-1 rounded-sm" style={{ height: `${20 + Math.abs(Math.sin(i * 0.6)) * 75}%`, backgroundColor: 'rgba(16,185,129,0.5)' }} />
                          ))}
                          <span className="absolute right-2 text-[7px] text-emerald-400 font-semibold">
                            {MUSIC_MOODS.find(m => m.id === audioConfig.mood)?.label} · {audioConfig.volume}%
                          </span>
                        </div>
                      ) : (
                        <div className="absolute inset-y-1.5 left-0 right-0 flex items-center px-2">
                          <span className="text-[8px] text-slate-700 italic">No music — select mood →</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* VOICE TRACK */}
                  <div className="flex items-center">
                    <div className="w-16 flex-shrink-0 px-1.5 py-1.5 flex items-center gap-1">
                      <i className="fas fa-microphone text-[8px] text-blue-400" />
                      <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wide">Voice</span>
                    </div>
                    <div className="flex-1 h-7 relative overflow-hidden">
                      {scriptData?.voiceover ? (
                        <div className="absolute inset-y-1 left-0 right-0 rounded flex items-center px-2 gap-px overflow-hidden"
                          style={{ backgroundColor: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.25)' }}>
                          {Array.from({ length: 60 }).map((_, i) => (
                            <div key={i} className="flex-1 rounded-sm" style={{ height: `${10 + Math.abs(Math.sin(i * 0.9 + 1.2)) * 80}%`, backgroundColor: 'rgba(124,58,237,0.5)' }} />
                          ))}
                          <span className="absolute right-2 text-[7px] text-blue-400 font-semibold">Voiceover script</span>
                        </div>
                      ) : (
                        <div className="absolute inset-y-1.5 left-0 right-0 flex items-center px-2">
                          <span className="text-[8px] text-slate-700 italic">Generate story →</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* PLAYHEAD */}
                  {(totalDuration || totalClipDuration) > 0 && (
                    <div className="absolute top-0 bottom-0 pointer-events-none z-20"
                      style={{ left: `calc(64px + ${(currentTime / (totalDuration || totalClipDuration)) * (100)}% * (100% - 64px) / 100%)` }}>
                      <div className="w-px h-full bg-red-500/90" />
                      <div className="absolute -top-0 -left-1.5 w-3 h-3 bg-red-500 rounded-full" />
                    </div>
                  )}
                </div>

                {/* Bottom info bar */}
                <div className="flex items-center justify-between px-3 py-1 border-t border-slate-800">
                  <span className="text-[8px] text-slate-600">
                    {enabledClips.length} clip{enabledClips.length !== 1 ? 's' : ''} · {fmtTime(totalClipDuration)}
                    {totalClipDuration > platform.maxDuration && (
                      <span className="text-red-400 ml-2"><i className="fas fa-exclamation-triangle mr-1" />Over {platform.name} limit</span>
                    )}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button onClick={addTextOverlay} className="px-2 py-0.5 text-[8px] font-semibold text-indigo-400 border border-indigo-800/60 rounded hover:bg-indigo-900/40 transition-all">
                      <i className="fas fa-plus text-[7px] mr-0.5" />Text
                    </button>
                    <button onClick={() => setControlTab('clips')} className="px-2 py-0.5 text-[8px] font-semibold text-slate-400 border border-slate-700 rounded hover:bg-slate-800 transition-all">
                      <i className="fas fa-upload text-[7px] mr-0.5" />Clip
                    </button>
                    <button onClick={() => setControlTab('audio')} className="px-2 py-0.5 text-[8px] font-semibold text-emerald-400 border border-emerald-900/60 rounded hover:bg-emerald-900/30 transition-all">
                      <i className="fas fa-music text-[7px] mr-0.5" />Music
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Control Panel — collapsible */}
            <div className={`flex flex-col border-l border-slate-200 bg-white transition-all relative flex-shrink-0`} style={{ width: panelOpen ? '40%' : 36 }}>
              {/* Collapse toggle */}
              <button
                onClick={() => setPanelOpen(o => !o)}
                className="absolute -left-3 top-1/2 -translate-y-1/2 z-20 w-6 h-10 flex items-center justify-center bg-white border border-slate-200 rounded-full shadow-md hover:bg-slate-50 transition-all"
                title={panelOpen ? 'Collapse panel' : 'Expand panel'}>
                <i className={`fas fa-chevron-${panelOpen ? 'right' : 'left'} text-slate-400 text-[8px]`} />
              </button>
              {panelOpen && (
                <>
              {/* Control tabs */}
              <div className="flex border-b border-slate-200 flex-shrink-0">
                {[
                  { id: 'text',    icon: 'fa-font',              label: 'Text' },
                  { id: 'audio',   icon: 'fa-music',             label: 'Music' },
                  { id: 'voice',   icon: 'fa-microphone',        label: 'Voice' },
                  { id: 'effects', icon: 'fa-palette',           label: 'Style' },
                  { id: 'clips',   icon: 'fa-film',              label: 'Clips' },
                  { id: 'publish', icon: 'fa-paper-plane',       label: 'Publish' },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setControlTab(tab.id)}
                    className={`flex-1 py-2 text-[8px] font-bold transition-all flex flex-col items-center gap-0.5 ${controlTab === tab.id ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400 hover:text-slate-600'}`}>
                    <i className={`fas ${tab.icon} text-[10px]`} />
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">

                {/* ── CLIPS TAB ── */}
                {controlTab === 'clips' && (
                  <>
                    <label className="flex flex-col items-center justify-center gap-2 py-4 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all group">
                      <input type="file" accept="video/mp4,video/quicktime,video/webm,video/x-msvideo" multiple className="hidden" onChange={handleClipUpload} />
                      <i className="fas fa-cloud-upload-alt text-slate-300 group-hover:text-blue-400 text-2xl transition-all" />
                      <span className="text-[11px] text-slate-500 font-semibold">Upload video clips</span>
                      <span className="text-[9px] text-slate-400">MP4, MOV, WebM · up to 500MB each</span>
                    </label>

                    {clips.length > 0 ? (
                      <div className="space-y-2">
                        <SH>Clips ({clips.length})</SH>
                        {clips.map((clip, ci) => (
                          <div key={clip.id} className={`rounded-xl border p-3 transition-all ${selectedClipId === clip.id ? 'border-blue-300 bg-blue-50' : 'border-slate-200'}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <button onClick={() => setClips(prev => prev.map(c => c.id === clip.id ? { ...c, enabled: !c.enabled } : c))}
                                className={`w-4 h-4 rounded flex items-center justify-center border text-[7px] transition-all flex-shrink-0 ${clip.enabled ? 'bg-slate-800 border-slate-800 text-white' : 'border-slate-300'}`}>
                                {clip.enabled && <i className="fas fa-check" />}
                              </button>
                              <input value={clip.label || clip.name}
                                onChange={e => setClips(prev => prev.map(c => c.id === clip.id ? { ...c, label: e.target.value } : c))}
                                className="flex-1 min-w-0 text-[11px] font-semibold text-slate-700 bg-transparent outline-none border-b border-transparent focus:border-slate-300" />
                              <button onClick={() => {
                                // Revoke the blob URL to free memory
                                if (clip.blobUrl) URL.revokeObjectURL(clip.blobUrl);
                                setClips(prev => prev.filter(c => c.id !== clip.id));
                              }}
                                className="text-red-400 hover:text-red-600 flex-shrink-0">
                                <i className="fas fa-trash text-[9px]" />
                              </button>
                            </div>
                            <p className="text-[9px] text-slate-400 mb-2">{fmtTime(clip.trimStart)} → {fmtTime(clip.trimEnd)} · {(clip.trimEnd - clip.trimStart).toFixed(1)}s used</p>
                            {/* Trim sliders */}
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[9px] text-slate-500 w-12">In {fmtTime(clip.trimStart)}</span>
                                <input type="range" min={0} max={clip.duration} step={0.1} value={clip.trimStart}
                                  onChange={e => setClips(prev => prev.map(c => c.id === clip.id ? { ...c, trimStart: Math.min(Number(e.target.value), c.trimEnd - 0.5) } : c))}
                                  className="flex-1 h-1 accent-blue-600 cursor-pointer" />
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[9px] text-slate-500 w-12">Out {fmtTime(clip.trimEnd)}</span>
                                <input type="range" min={0} max={clip.duration} step={0.1} value={clip.trimEnd}
                                  onChange={e => setClips(prev => prev.map(c => c.id === clip.id ? { ...c, trimEnd: Math.max(Number(e.target.value), c.trimStart + 0.5) } : c))}
                                  className="flex-1 h-1 accent-blue-600 cursor-pointer" />
                              </div>
                            </div>
                            {/* Load into player */}
                            <button onClick={() => { if (videoRef.current && clip.blobUrl) { videoRef.current.src = clip.blobUrl; videoRef.current.currentTime = clip.trimStart; setCurrentTime(clip.trimStart); setSelectedClipId(clip.id); } }}
                              className="mt-2 w-full py-1 text-[9px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all">
                              <i className="fas fa-play text-[8px] mr-1" />Preview this clip
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <i className="fas fa-film text-slate-200 text-3xl mb-2" />
                        <p className="text-xs text-slate-400">Upload your footage above</p>
                      </div>
                    )}
                  </>
                )}

                {/* ── TEXT OVERLAYS TAB ── */}
                {controlTab === 'text' && (
                  <>
                    {/* ── COVER IMAGE ── */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="px-3 py-2 bg-slate-50 flex items-center gap-2">
                        <i className="fas fa-image text-slate-400 text-[10px]" />
                        <p className="text-[10px] font-bold text-slate-600 flex-1">Cover Image</p>
                        <span className="text-[9px] text-slate-400">Shows 0–3s</span>
                        {coverImageUrl && (
                          <button onClick={() => setCoverImageUrl('')}
                            className="text-red-400 hover:text-red-600 text-[9px]">
                            <i className="fas fa-times" />
                          </button>
                        )}
                      </div>
                      {coverImageUrl ? (
                        <div className="relative">
                          <img src={coverImageUrl} alt="Cover" className="w-full h-28 object-cover" />
                          <div className="absolute bottom-1 right-1">
                            <label className="px-2 py-1 bg-black/60 text-white text-[9px] rounded-lg cursor-pointer hover:bg-black/80">
                              <i className="fas fa-exchange-alt text-[8px] mr-1" />Replace
                              <input type="file" accept="image/*" className="hidden" onChange={e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const reader = new FileReader();
                                reader.onload = ev => { setCoverImageUrl(ev.target.result); triggerAutoSave(); };
                                reader.readAsDataURL(file);
                                e.target.value = '';
                              }} />
                            </label>
                          </div>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center gap-1.5 py-4 cursor-pointer hover:bg-slate-50 transition-all">
                          <input type="file" accept="image/*" className="hidden" onChange={e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = ev => { setCoverImageUrl(ev.target.result); triggerAutoSave(); };
                            reader.readAsDataURL(file);
                            e.target.value = '';
                          }} />
                          <div className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center">
                            <i className="fas fa-cloud-upload-alt text-indigo-400 text-sm" />
                          </div>
                          <p className="text-[10px] font-semibold text-slate-500">Upload cover image</p>
                          <p className="text-[9px] text-slate-400">JPG, PNG · embedded in first 3 seconds</p>
                        </label>
                      )}
                    </div>

                    <button onClick={addTextOverlay}
                      className="w-full py-2.5 text-[11px] font-bold text-white bg-blue-700 rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-sm">
                      <i className="fas fa-plus text-[10px]" />Add Text at {fmtTime(currentTime)}
                    </button>

                    {/* ── Global Text Style ── */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="px-3 py-2 bg-slate-50 flex items-center gap-1.5">
                        <i className="fas fa-text-height text-slate-400 text-[10px]" />
                        <p className="text-[10px] font-bold text-slate-600 flex-1">Global Text Style</p>
                        <span className="text-[9px] text-slate-400">Applies to all texts</span>
                      </div>
                      <div className="px-3 py-2.5 space-y-2.5">
                        {/* Font Family */}
                        <div>
                          <p className="text-[9px] text-slate-500 font-semibold mb-1.5 uppercase tracking-wide">Font Family</p>
                          <div className="relative">
                            <select
                              value={globalTextStyle.fontFamily}
                              onChange={e => setGlobalTextStyle(prev => ({ ...prev, fontFamily: e.target.value }))}
                              className="w-full px-2.5 py-1.5 text-[11px] border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 bg-white appearance-none cursor-pointer pr-7"
                              style={{ fontFamily: globalTextStyle.fontFamily }}>
                              {FONT_OPTIONS.map(f => (
                                <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                                  {f.label}
                                </option>
                              ))}
                            </select>
                            <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[8px] pointer-events-none" />
                          </div>
                          {/* Font preview */}
                          <p className="mt-1.5 text-[13px] text-slate-700 truncate px-1"
                            style={{ fontFamily: globalTextStyle.fontFamily, fontWeight: globalTextStyle.bold ? 800 : 600 }}>
                            Your Text Preview
                          </p>
                        </div>
                        {/* Font Size + Bold */}
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <p className="text-[9px] text-slate-500 font-semibold mb-1 uppercase tracking-wide">Size</p>
                            <div className="flex gap-1 flex-wrap">
                              {FONT_SIZES.map(sz => (
                                <button key={sz}
                                  onClick={() => setGlobalTextStyle(prev => ({ ...prev, fontSize: sz }))}
                                  className={`px-2 py-0.5 text-[9px] font-bold rounded-lg border transition-all ${globalTextStyle.fontSize === sz ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-400'}`}>
                                  {sz}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="flex-shrink-0 mt-3.5">
                            <button
                              onClick={() => setGlobalTextStyle(prev => ({ ...prev, bold: !prev.bold }))}
                              className={`w-9 h-9 text-sm font-black rounded-xl border-2 transition-all ${globalTextStyle.bold ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 text-slate-600 hover:border-slate-500'}`}>
                              B
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {textOverlays.length > 0 ? (
                      <div className="space-y-2">
                        {textOverlays.map(ov => (
                          <div key={ov.id} className={`border rounded-xl overflow-hidden transition-all ${editingOverlayId === ov.id ? 'border-indigo-300 bg-indigo-50/30' : 'border-slate-200'}`}>
                            {/* Row — div not button so nested controls work */}
                            <div className="w-full px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-slate-50"
                              onClick={() => setEditingOverlayId(editingOverlayId === ov.id ? null : ov.id)}>
                              <div className="w-5 h-5 rounded bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                <i className="fas fa-font text-indigo-500 text-[8px]" />
                              </div>
                              <span className="text-[11px] font-medium text-slate-700 flex-1 truncate">{ov.text}</span>
                              <span className="text-[9px] text-slate-400 flex-shrink-0">{fmtTime(ov.startTime)}</span>
                              <i className={`fas fa-chevron-${editingOverlayId === ov.id ? 'up' : 'down'} text-slate-300 text-[8px] flex-shrink-0`} />
                              <button onClick={e => { e.stopPropagation(); removeOverlay(ov.id); }}
                                className="text-red-400 hover:text-red-600 flex-shrink-0 p-1">
                                <i className="fas fa-trash text-[9px]" />
                              </button>
                            </div>
                            {editingOverlayId === ov.id && (
                              <div className="px-3 pb-3 space-y-2.5 border-t border-slate-100 pt-2.5 bg-slate-50/50"
                                onClick={e => e.stopPropagation()}>
                                <input
                                  value={ov.text}
                                  onChange={e => updateOverlay(ov.id, { text: e.target.value })}
                                  placeholder="Type your text here…"
                                  className="w-full px-3 py-2 text-sm font-medium border-2 border-indigo-400 rounded-xl focus:outline-none focus:border-indigo-600 bg-white text-slate-800 shadow-sm"
                                />
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <p className="text-[9px] text-slate-500 mb-1">Start (s)</p>
                                    <input type="number" step="0.1" min="0" value={ov.startTime} onChange={e => updateOverlay(ov.id, { startTime: Number(e.target.value) })}
                                      className="w-full px-2 py-1 text-[10px] border border-slate-200 rounded-lg focus:outline-none" />
                                  </div>
                                  <div>
                                    <p className="text-[9px] text-slate-500 mb-1">Duration (s)</p>
                                    <input type="number" step="0.5" min="0.5" value={ov.duration} onChange={e => updateOverlay(ov.id, { duration: Number(e.target.value) })}
                                      className="w-full px-2 py-1 text-[10px] border border-slate-200 rounded-lg focus:outline-none" />
                                  </div>
                                </div>
                                <div className="flex gap-1.5">
                                  {['top', 'center', 'bottom'].map(pos => (
                                    <button key={pos} onClick={() => updateOverlay(ov.id, { position: pos })}
                                      className={`flex-1 py-1 text-[9px] font-semibold rounded-lg border capitalize transition-all ${ov.position === pos ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600'}`}>
                                      {pos}
                                    </button>
                                  ))}
                                </div>
                                <div className="flex items-center gap-2">
                                  <input type="color" value={ov.color} onChange={e => updateOverlay(ov.id, { color: e.target.value })}
                                    className="w-8 h-8 rounded border border-slate-200 cursor-pointer flex-shrink-0" />
                                  <input type="number" value={ov.fontSize} min={12} max={72} onChange={e => updateOverlay(ov.id, { fontSize: Number(e.target.value) })}
                                    className="w-14 px-2 py-1 text-[10px] border border-slate-200 rounded-lg text-center" />
                                  <button onClick={() => updateOverlay(ov.id, { bold: !ov.bold })}
                                    className={`w-8 h-8 font-black text-sm rounded-lg border transition-all ${ov.bold ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-500'}`}>
                                    B
                                  </button>
                                </div>
                                <div className="grid grid-cols-3 gap-1">
                                  {['none', 'box', 'bar'].map(bg => (
                                    <button key={bg} onClick={() => updateOverlay(ov.id, { background: bg })}
                                      className={`py-1 text-[9px] font-semibold rounded-lg border capitalize transition-all ${ov.background === bg ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600'}`}>
                                      {bg === 'none' ? 'No BG' : bg === 'box' ? 'Box' : 'Bar'}
                                    </button>
                                  ))}
                                </div>
                                <div className="grid grid-cols-2 gap-1">
                                  {TEXT_ANIM.map(a => (
                                    <button key={a.id} onClick={() => updateOverlay(ov.id, { animation: a.id })}
                                      className={`py-1 text-[9px] font-semibold rounded-lg border transition-all ${ov.animation === a.id ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600'}`}>
                                      {a.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <i className="fas fa-font text-slate-200 text-3xl mb-2" />
                        <p className="text-xs text-slate-400">Press the button above to add text at the current playhead position</p>
                      </div>
                    )}
                  </>
                )}

                {/* ── AUDIO TAB ── */}
                {controlTab === 'audio' && (
                  <>
                    {audioConfig.mood !== 'none' && !customMusicFile && (
                      <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-2">
                        <i className="fas fa-music text-blue-500 text-xs flex-shrink-0" />
                        <p className="text-[9px] text-blue-700">
                          <strong>AI beat playing</strong> — hear synthesised music while you edit. Upload your own track below to replace it.
                        </p>
                      </div>
                    )}
                    <div>
                      <SH>Music Mood</SH>
                      <div className="grid grid-cols-2 gap-1.5">
                        {MUSIC_MOODS.map(m => (
                          <button key={m.id} onClick={() => {
                            setAudioConfig(prev => ({ ...prev, mood: m.id }));
                            setMusicPreviewPlaying(false);
                            triggerAutoSave();
                            // Reset video to beginning so music syncs from the start
                            if (videoRef.current && m.id !== 'none') {
                              setIsPlaying(false);
                              videoRef.current.currentTime = 0;
                              setCurrentTime(0);
                              stopMusic();
                            }
                          }}
                            className={`px-2 py-2 rounded-xl border text-left transition-all ${audioConfig.mood === m.id ? 'border-slate-800 bg-slate-800' : 'border-slate-200 hover:border-slate-300'}`}>
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <i className={`fas ${m.icon} text-[10px]`} style={{ color: audioConfig.mood === m.id ? '#fff' : m.color }} />
                              <span className={`text-[10px] font-bold ${audioConfig.mood === m.id ? 'text-white' : 'text-slate-700'}`}>{m.label}</span>
                            </div>
                            <span className={`text-[8px] ${audioConfig.mood === m.id ? 'text-slate-300' : 'text-slate-400'}`}>{m.desc}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {audioConfig.mood !== 'none' && (
                      <>
                        <div>
                          <div className="flex justify-between text-[11px] mb-1">
                            <span className="font-medium text-slate-600">Music Volume</span>
                            <span className="text-slate-400">{audioConfig.volume}%</span>
                          </div>
                          <input type="range" min={0} max={100} value={audioConfig.volume}
                            onChange={e => { setAudioConfig(prev => ({ ...prev, volume: Number(e.target.value) })); triggerAutoSave(); }}
                            className="w-full h-1.5 accent-slate-800 cursor-pointer" />
                        </div>

                        <div>
                          <div className="flex justify-between text-[11px] mb-1">
                            <span className="font-medium text-slate-600">Original Audio</span>
                            <span className="text-slate-400">{audioConfig.videoVolume}%</span>
                          </div>
                          <input type="range" min={0} max={100} value={audioConfig.videoVolume ?? 100}
                            onChange={e => { setAudioConfig(prev => ({ ...prev, videoVolume: Number(e.target.value) })); triggerAutoSave(); }}
                            className="w-full h-1.5 accent-slate-800 cursor-pointer" />
                        </div>

                        <Toggle on={audioConfig.autoDuck} onToggle={() => { setAudioConfig(prev => ({ ...prev, autoDuck: !prev.autoDuck })); triggerAutoSave(); }}
                          label="Auto-Duck" sub="Lower music volume during voiceover/speech" />
                        <Toggle on={audioConfig.fadeIn} onToggle={() => { setAudioConfig(prev => ({ ...prev, fadeIn: !prev.fadeIn })); triggerAutoSave(); }}
                          label="Fade In" sub="Music gradually rises at start" />
                        <Toggle on={audioConfig.fadeOut} onToggle={() => { setAudioConfig(prev => ({ ...prev, fadeOut: !prev.fadeOut })); triggerAutoSave(); }}
                          label="Fade Out" sub="Music gradually fades at end" />
                      </>
                    )}

                    <div>
                      <SH>Upload Custom Audio</SH>
                      <label className="flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-all">
                        <input type="file" accept="audio/*" className="hidden" onChange={e => {
                          if (e.target.files[0]) {
                            const file = e.target.files[0];
                            setCustomMusicFile(file);
                            if (audioRef.current) {
                              audioRef.current.src = URL.createObjectURL(file);
                              audioRef.current.loop = true;
                            }
                            setAudioConfig(prev => ({ ...prev, mood: 'custom' }));
                            triggerAutoSave();
                          }
                        }} />
                        <i className="fas fa-upload text-slate-400 text-xs" />
                        <span className="text-[11px] text-slate-500 font-medium">
                          {customMusicFile ? customMusicFile.name : 'Upload MP3 / AAC / WAV'}
                        </span>
                      </label>
                      {/* Preview music button (custom file) */}
                      {customMusicFile && (
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => {
                              const aud = audioRef.current;
                              if (!aud) return;
                              if (musicPreviewPlaying) {
                                aud.pause();
                                aud.currentTime = 0;
                                setMusicPreviewPlaying(false);
                              } else {
                                aud.volume = audioConfig.volume / 100;
                                aud.play()
                                  .then(() => setMusicPreviewPlaying(true))
                                  .catch(() => setMusicPreviewPlaying(false));
                              }
                            }}
                            className="flex-1 py-2 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all flex items-center justify-center gap-1.5">
                            {musicPreviewPlaying
                              ? <><i className="fas fa-stop text-[9px]" />Stop Preview</>
                              : <><i className="fas fa-play text-[9px]" />Preview Custom Track</>
                            }
                          </button>
                        </div>
                      )}
                      {/* Preview mood music when NOT using a custom file */}
                      {!customMusicFile && audioConfig.mood !== 'none' && (
                        <div className="mt-2">
                          <button
                            onClick={() => {
                              if (musicPreviewPlaying) {
                                stopMusic();
                                setMusicPreviewPlaying(false);
                              } else {
                                startMusic();
                                setMusicPreviewPlaying(true);
                              }
                            }}
                            className="w-full py-2 text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-all flex items-center justify-center gap-1.5">
                            {musicPreviewPlaying
                              ? <><i className="fas fa-stop text-[9px]" />Stop Preview</>
                              : <><i className="fas fa-play text-[9px]" />Preview {audioConfig.mood} track</>
                            }
                          </button>
                        </div>
                      )}
                      {/* audio element moved to component root so it persists across all tabs */}
                    </div>
                  </>
                )}

                {/* ── EFFECTS TAB ── */}
                {controlTab === 'effects' && (
                  <>
                    <div>
                      <SH>Colour Grade</SH>
                      <div className="grid grid-cols-4 gap-1.5">
                        {FILTER_PRESETS.map(f => (
                          <button key={f.id} onClick={() => { setEffectsConfig(prev => ({ ...prev, filter: f.id })); triggerAutoSave(); }}
                            className={`py-2.5 text-[9px] font-bold rounded-xl border transition-all ${effectsConfig.filter === f.id ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            {f.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {[
                      { key: 'brightness', label: 'Brightness', min: -0.5, max: 0.5, step: 0.05 },
                      { key: 'contrast',   label: 'Contrast',   min: -0.5, max: 0.5, step: 0.05 },
                      { key: 'saturation', label: 'Saturation', min: -0.5, max: 0.5, step: 0.05 },
                    ].map(slider => (
                      <div key={slider.key}>
                        <div className="flex justify-between text-[11px] mb-1">
                          <span className="font-medium text-slate-600">{slider.label}</span>
                          <span className="text-slate-400">{effectsConfig[slider.key] >= 0 ? '+' : ''}{(effectsConfig[slider.key] * 100).toFixed(0)}%</span>
                        </div>
                        <input type="range" min={slider.min} max={slider.max} step={slider.step} value={effectsConfig[slider.key]}
                          onChange={e => { setEffectsConfig(prev => ({ ...prev, [slider.key]: Number(e.target.value) })); triggerAutoSave(); }}
                          className="w-full h-1.5 accent-slate-800 cursor-pointer" />
                      </div>
                    ))}

                    <div>
                      <SH>Transition Between Clips</SH>
                      <div className="grid grid-cols-5 gap-1">
                        {TRANSITIONS.map(t => (
                          <button key={t.id} onClick={() => { setEffectsConfig(prev => ({ ...prev, transition: t.id })); triggerAutoSave(); }}
                            className={`py-2 rounded-xl border text-center transition-all ${effectsConfig.transition === t.id ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                            <i className={`${t.icon} block text-[10px] mb-0.5`} />
                            <span className="text-[8px] font-medium">{t.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <Toggle on={effectsConfig.stabilise} onToggle={() => { setEffectsConfig(prev => ({ ...prev, stabilise: !prev.stabilise })); triggerAutoSave(); }}
                      label="Stabilisation" sub="Reduce camera shake (processing required)" />
                  </>
                )}

                {/* ── CAPTIONS TAB ── */}
                {controlTab === 'caption' && (
                  <>
                    <div>
                      <SH>Caption Style</SH>
                      {CAPTION_STYLES.map(s => (
                        <button key={s.id} onClick={() => { setCaptionConfig(prev => ({ ...prev, style: s.id })); triggerAutoSave(); }}
                          className={`w-full text-left px-3 py-2.5 rounded-xl border mb-1.5 transition-all ${captionConfig.style === s.id ? 'bg-slate-900 border-slate-900' : 'border-slate-200 hover:border-slate-300'}`}>
                          <span className={`text-[11px] font-bold block ${captionConfig.style === s.id ? 'text-white' : 'text-slate-700'}`}>{s.label}</span>
                          <span className={`text-[9px] ${captionConfig.style === s.id ? 'text-slate-400' : 'text-slate-400'}`}>{s.desc}</span>
                        </button>
                      ))}
                    </div>

                    {captionConfig.style !== 'none' && (
                      <>
                        <div>
                          <SH>Position</SH>
                          <div className="flex gap-1.5">
                            {['top', 'center', 'bottom'].map(p => (
                              <button key={p} onClick={() => { setCaptionConfig(prev => ({ ...prev, position: p })); triggerAutoSave(); }}
                                className={`flex-1 py-1.5 text-[10px] font-semibold rounded-xl border capitalize transition-all ${captionConfig.position === p ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600'}`}>
                                {p}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <SH>Background</SH>
                          <div className="flex gap-1.5">
                            {[{ id: 'none', label: 'None' }, { id: 'box', label: 'Box' }, { id: 'bar', label: 'Bar' }].map(b => (
                              <button key={b.id} onClick={() => { setCaptionConfig(prev => ({ ...prev, background: b.id })); triggerAutoSave(); }}
                                className={`flex-1 py-1.5 text-[10px] font-semibold rounded-xl border transition-all ${captionConfig.background === b.id ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600'}`}>
                                {b.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <div className="flex justify-between text-[11px] mb-1">
                            <span className="font-medium text-slate-600">Font Size</span>
                            <span className="text-slate-400">{captionConfig.size}px</span>
                          </div>
                          <input type="range" min={14} max={56} value={captionConfig.size}
                            onChange={e => { setCaptionConfig(prev => ({ ...prev, size: Number(e.target.value) })); triggerAutoSave(); }}
                            className="w-full h-1.5 accent-slate-800 cursor-pointer" />
                        </div>

                        <div>
                          <SH>Caption Colour</SH>
                          <div className="flex items-center gap-2">
                            <input type="color" value={captionConfig.color}
                              onChange={e => { setCaptionConfig(prev => ({ ...prev, color: e.target.value })); triggerAutoSave(); }}
                              className="w-8 h-8 rounded border border-slate-200 cursor-pointer" />
                            <button onClick={() => { setCaptionConfig(prev => ({ ...prev, color: '#ffffff' })); triggerAutoSave(); }}
                              className="px-2 py-1 text-[9px] border border-slate-200 rounded-lg bg-white text-slate-600">White</button>
                            <button onClick={() => { setCaptionConfig(prev => ({ ...prev, color: '#000000' })); triggerAutoSave(); }}
                              className="px-2 py-1 text-[9px] border border-slate-200 rounded-lg bg-white text-slate-600">Black</button>
                            {Object.values(brandColors).map((c, i) => (
                              <button key={i} onClick={() => { setCaptionConfig(prev => ({ ...prev, color: c })); triggerAutoSave(); }}
                                className="w-7 h-7 rounded-lg border-2 border-white ring-1 ring-slate-200 hover:ring-slate-400" style={{ backgroundColor: c }} />
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* ── VOICE TAB ── */}
                {controlTab === 'voice' && (
                  <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center">
                      <i className="fas fa-microphone text-purple-500 text-2xl" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-slate-800">AI Voice Coming Soon</p>
                      <p className="text-xs text-slate-500 mt-1">Voiceover generation will be available in a future update.</p>
                    </div>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 text-[10px] font-bold rounded-full">Coming Soon</span>
                  </div>
                )}

                {/* ── VOICE TAB (hidden placeholder for state vars) ── */}
                {false && controlTab === 'voice_disabled' && (
                  <div className="space-y-3">
                    {outputMode === 'music-only' && (
                      <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                        <i className="fas fa-info-circle text-amber-500 text-xs mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-[10px] font-bold text-amber-800">Music-Only Mode Active</p>
                          <p className="text-[9px] text-amber-700 mt-0.5">Voice preview is for scripting reference only. Switch to <strong>Voice + Music</strong> to include voiceover in the export.</p>
                        </div>
                      </div>
                    )}

                    {/* ── Gender toggle ── */}
                    <div>
                      <SH>Voice Gender</SH>
                      <div className="flex rounded-xl overflow-hidden border border-slate-200 w-full">
                        {[
                          { id: 'female', label: 'Female', icon: 'fa-venus' },
                          { id: 'male',   label: 'Male',   icon: 'fa-mars'  },
                        ].map(g => (
                          <button key={g.id}
                            onClick={() => {
                              setVoiceGender(g.id);
                              // Switch to first voice of that gender
                              setTtsVoice(TTS_VOICES[g.id][0].id);
                            }}
                            className={`flex-1 py-2 flex items-center justify-center gap-1.5 text-[10px] font-bold transition-all
                              ${voiceGender === g.id
                                ? (g.id === 'female' ? 'bg-blue-700 text-white' : 'bg-slate-700 text-white')
                                : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                            <i className={`fas ${g.icon} text-[9px]`} />
                            {g.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* ── Voice selector (3 per gender) ── */}
                    <div>
                      <SH>AI Voice — OpenAI TTS</SH>
                      <div className="space-y-1.5">
                        {TTS_VOICES[voiceGender].map(v => (
                          <button key={v.id}
                            onClick={() => setTtsVoice(v.id)}
                            className={`w-full p-2.5 rounded-xl border text-left transition-all flex items-center gap-2
                              ${ttsVoice === v.id
                                ? 'bg-blue-800 border-blue-700'
                                : 'border-slate-200 hover:border-blue-300 bg-white'}`}>
                            <i className={`fas fa-microphone text-[10px] flex-shrink-0 ${ttsVoice === v.id ? 'text-blue-300' : 'text-slate-400'}`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-[10px] font-bold ${ttsVoice === v.id ? 'text-white' : 'text-slate-700'}`}>{v.label}</p>
                              <p className={`text-[8px] mt-0.5 ${ttsVoice === v.id ? 'text-blue-200' : 'text-slate-400'}`}>{v.desc}</p>
                            </div>
                            {ttsVoice === v.id && (
                              <i className="fas fa-check-circle text-blue-300 text-xs flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* ── TTS Status / Debug Panel ── */}
                    <div className={`px-3 py-2.5 rounded-xl border text-[9px] font-mono
                      ${ttsStatus === 'error'   ? 'bg-red-50 border-red-200 text-red-700'   :
                        ttsStatus === 'playing' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                        ttsStatus === 'loading' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                        'bg-slate-50 border-slate-200 text-slate-500'}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <i className={`fas text-[8px]
                          ${ttsStatus === 'error'   ? 'fa-exclamation-circle text-red-500'   :
                            ttsStatus === 'playing' ? 'fa-volume-up text-emerald-500 animate-pulse' :
                            ttsStatus === 'loading' ? 'fa-spinner fa-spin text-blue-500' :
                            'fa-info-circle text-slate-400'}`} />
                        <span className="font-bold uppercase tracking-wide text-[8px]">Voice Status</span>
                      </div>
                      <p className="leading-relaxed">
                        {ttsStatus === 'idle'    && `Voice: ${ttsVoice} · Gender: ${voiceGender} · Ready`}
                        {ttsStatus === 'loading' && `Requesting OpenAI TTS — voice: ${ttsVoice} · Please wait…`}
                        {ttsStatus === 'playing' && `Playing · Voice: ${ttsVoice} (${voiceGender}) · OpenAI TTS`}
                        {ttsStatus === 'error'   && (ttsError || 'Voice synthesis failed')}
                      </p>
                    </div>

                    {/* Script preview */}
                    {scriptData?.voiceover ? (
                      <div className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl max-h-28 overflow-y-auto">
                        <p className="text-[9px] text-slate-400 uppercase font-bold tracking-wide mb-1">Script to Speak</p>
                        <p className="text-[11px] text-slate-700 leading-relaxed">{scriptData.voiceover}</p>
                      </div>
                    ) : (
                      <div className="px-3 py-3 bg-amber-50 border border-amber-200 rounded-xl text-center">
                        <i className="fas fa-scroll text-amber-400 text-sm mb-1" />
                        <p className="text-[10px] text-amber-700 font-medium">Generate your story script first</p>
                        <button onClick={() => setActiveSection('script')}
                          className="mt-1.5 text-[9px] text-amber-600 underline">Go to Story →</button>
                      </div>
                    )}

                    {/* Playback controls */}
                    <div className="space-y-2">
                      {ttsStatus !== 'playing' ? (
                        <button onClick={handlePreviewVoice}
                          disabled={!scriptData?.voiceover || ttsStatus === 'loading'}
                          className="w-full py-3 bg-blue-700 hover:bg-blue-800 text-white text-[11px] font-bold rounded-xl disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-sm">
                          {ttsStatus === 'loading' ? (
                            <><i className="fas fa-spinner fa-spin text-[10px]" />Generating voice…</>
                          ) : (
                            <><i className="fas fa-play text-[10px]" />Preview Voiceover</>
                          )}
                        </button>
                      ) : (
                        <button onClick={handleStopVoice}
                          className="w-full py-3 bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold rounded-xl transition-all flex items-center justify-center gap-2">
                          <i className="fas fa-stop text-[10px]" />
                          Stop Preview
                        </button>
                      )}
                      {ttsStatus === 'playing' && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl">
                          <div className="flex gap-0.5 items-end">
                            {[3, 5, 4, 6, 3, 5, 4].map((h, i) => (
                              <div key={i} className="w-1 bg-blue-400 rounded-full animate-bounce" style={{ height: h*3, animationDelay: `${i*0.08}s` }} />
                            ))}
                          </div>
                          <span className="text-[10px] text-blue-700 font-medium">Playing OpenAI TTS audio…</span>
                        </div>
                      )}
                    </div>

                    <div className="px-3 py-2.5 bg-slate-50 border border-dashed border-slate-200 rounded-xl">
                      <p className="text-[9px] text-slate-400 text-center leading-relaxed">
                        Using OpenAI TTS with British-feel voices. Upload your own recording in the <strong>Music</strong> tab to override.
                      </p>
                    </div>
                  </div>
                )}

                {/* ── PUBLISH TAB ── */}
                {controlTab === 'publish' && (
                  <div className="space-y-4">
                    {/* Output mode badge */}
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${outputMode === 'music-only' ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}`}>
                      <i className={`fas ${outputMode === 'music-only' ? 'fa-music' : 'fa-microphone'} text-xs ${outputMode === 'music-only' ? 'text-emerald-600' : 'text-blue-600'}`} />
                      <div className="flex-1">
                        <p className={`text-[10px] font-bold ${outputMode === 'music-only' ? 'text-emerald-800' : 'text-blue-800'}`}>
                          {outputMode === 'music-only' ? 'Music-Only Export' : 'Voice + Music Export'}
                        </p>
                        <p className={`text-[9px] ${outputMode === 'music-only' ? 'text-emerald-600' : 'text-blue-600'}`}>
                          {outputMode === 'music-only'
                            ? 'Text overlays tell your story. Ensure captions are on.'
                            : 'Voiceover + background music will be baked in.'}
                        </p>
                      </div>
                    </div>

                    {/* Production summary */}
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mb-2">Production Summary</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {[
                          { icon: 'fa-expand',    label: 'Format',  value: `${aspectRatio} · ${fmtTime(totalClipDuration)}` },
                          { icon: 'fa-palette',   label: 'Style',   value: FILTER_PRESETS.find(f => f.id === effectsConfig.filter)?.label || 'Natural' },
                          { icon: 'fa-music',     label: 'Music',   value: MUSIC_MOODS.find(m => m.id === audioConfig.mood)?.label || 'None' },
                          { icon: 'fa-font',      label: 'Overlays',value: `${textOverlays.length} text card${textOverlays.length !== 1 ? 's' : ''}` },
                          { icon: 'fa-film',      label: 'Clips',   value: `${enabledClips.length} clip${enabledClips.length !== 1 ? 's' : ''}` },
                          { icon: 'fa-microphone',label: 'Mode',    value: outputMode === 'music-only' ? 'Music Only' : 'Voice + Music' },
                        ].map(item => (
                          <div key={item.label} className="flex items-center gap-1.5">
                            <i className={`fas ${item.icon} text-slate-400 text-[9px] w-3`} />
                            <div>
                              <p className="text-[8px] text-slate-400 uppercase leading-none">{item.label}</p>
                              <p className="text-[10px] font-semibold text-slate-700">{item.value}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Caption */}
                    <div>
                      <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wide block mb-1.5">Post Caption</label>
                      <textarea value={caption} onChange={e => { setCaption(e.target.value); triggerAutoSave(); }}
                        rows={4} placeholder="Write your post caption…"
                        className="w-full text-xs px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 resize-none leading-relaxed" />
                    </div>

                    {/* Hashtags */}
                    <div>
                      <SH>Hashtags</SH>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {hashtags.map((tag, i) => (
                          <button key={i} onClick={() => toggleHashtag(tag)}
                            className="px-2 py-0.5 text-[9px] font-medium bg-slate-800 text-white rounded-lg flex items-center gap-0.5 hover:bg-slate-700">
                            #{tag} <i className="fas fa-times text-[6px] opacity-60" />
                          </button>
                        ))}
                        {hashtags.length === 0 && <p className="text-[9px] text-slate-400 italic">No hashtags yet — generate a story first</p>}
                      </div>
                    </div>

                    {/* Platform limit warning */}
                    {totalClipDuration > platform.maxDuration && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
                        <i className="fas fa-exclamation-triangle text-amber-500 text-xs" />
                        <p className="text-[10px] text-amber-700">
                          Video is {fmtTime(totalClipDuration - platform.maxDuration)} over the {platform.name} limit. Trim in Clips tab.
                        </p>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="pb-2">
                      {(() => {
                        const blobReady = !!exportedBlobRef.current;
                        const rendering = exportStatus === 'rendering';
                        const errored   = exportStatus === 'error';
                        // When blob is ready: clicking opens Publish modal (not re-export)
                        const handleClick = blobReady
                          ? () => { setShowPublishConfirm(true); setPublishError(''); }
                          : handleDownloadVideo;
                        const bgClass = rendering ? 'bg-blue-800 cursor-not-allowed'
                          : blobReady  ? 'bg-emerald-600 hover:bg-emerald-700'
                          : errored    ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-slate-700 hover:bg-slate-600';
                        return (
                          <button onClick={handleClick}
                            disabled={!enabledClips.length || rendering}
                            className={`w-full py-3 text-white text-[11px] font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 overflow-hidden relative ${bgClass}`}>
                            {rendering ? (
                              <>
                                <div className="absolute inset-0 bg-blue-500/40 transition-all duration-300" style={{ width: `${exportProgress}%` }} />
                                <i className="fas fa-circle-notch fa-spin text-[10px] relative z-10" />
                                <span className="relative z-10">Rendering {exportProgress}%…</span>
                              </>
                            ) : blobReady ? (
                              <><i className="fas fa-paper-plane text-[10px]" />Publish Video</>
                            ) : errored ? (
                              <><i className="fas fa-exclamation-triangle text-[10px]" />Export failed — try again</>
                            ) : (
                              <><i className="fas fa-film text-[10px]" />Finalise Video</>
                            )}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* ── THUMBNAIL / COVER TAB ── */}
                {controlTab === 'thumb' && (
                  <>
                    <canvas ref={thumbCanvasRef} className="hidden" />

                    {thumbnailUrl ? (
                      <div className="space-y-3">
                        <div className="rounded-xl overflow-hidden border border-slate-200">
                          <img src={thumbnailUrl} alt="Thumbnail" className="w-full object-cover max-h-40" />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={handleCaptureFrame} className="flex-1 py-2 text-[10px] font-semibold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all">
                            <i className="fas fa-camera text-[9px] mr-1" />Recapture Frame
                          </button>
                          <button onClick={() => { setThumbnailUrl(''); setThumbnailMode('none'); }} className="px-3 py-2 text-[10px] text-red-500 border border-red-200 rounded-xl hover:bg-red-50 transition-all">
                            <i className="fas fa-trash text-[9px]" />
                          </button>
                        </div>
                        <button onClick={() => setThumbnailMode(thumbnailMode === 'studio' ? 'frame' : 'studio')}
                          className="w-full py-2.5 text-[10px] font-bold text-white bg-blue-700 rounded-xl  transition-all flex items-center justify-center gap-1.5">
                          <i className="fas fa-wand-magic-sparkles text-[9px]" />
                          {thumbnailMode === 'studio' ? 'Exit Design Studio' : 'Open Design Studio'}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <button onClick={handleCaptureFrame} disabled={!enabledClips.length}
                          className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-[11px] font-semibold text-slate-600 hover:border-blue-400 hover:bg-blue-50 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
                          <i className="fas fa-camera text-slate-400" />Capture Frame from Video
                        </button>
                        <button onClick={() => setThumbnailMode('studio')}
                          className="w-full py-3 bg-blue-700 text-white text-[11px] font-bold rounded-xl flex items-center justify-center gap-2  transition-all shadow-md shadow-blue-200">
                          <i className="fas fa-wand-magic-sparkles" />Design Custom Cover Art
                        </button>
                      </div>
                    )}

                    {thumbnailMode === 'studio' && (
                      <div className="border border-blue-200 rounded-xl overflow-hidden" style={{ height: 480 }}>
                        <DesignStudio
                          day={day}
                          businessData={businessData}
                          baseImageUrl={thumbnailUrl}
                          onSaveDesign={(designData, exportUrl) => {
                            setThumbnailDesign(designData);
                            if (exportUrl) setThumbnailUrl(exportUrl);
                            setThumbnailMode('frame');
                            triggerAutoSave();
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
                </>
              )}
            </div>
            </div>

          </div>
        )}

        {/* ─── PREVIEW SECTION (legacy — kept for any old section refs) ─── */}
        {activeSection === 'preview' && (
          <div className="h-full overflow-y-auto p-5 max-w-2xl mx-auto space-y-5">

            {/* Platform mock */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: platform.color }}>
                  <i className={`${platform.icon} text-white text-sm`} />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">{businessData?.businessName || 'Your Business'}</p>
                  <p className="text-[10px] text-slate-400">{platform.name}</p>
                </div>
              </div>

              {/* Video thumbnail preview */}
              <div className="bg-slate-900 aspect-[9/16] max-h-80 flex items-center justify-center relative overflow-hidden">
                {thumbnailUrl ? (
                  <img src={thumbnailUrl} alt="Cover" className="w-full h-full object-cover" />
                ) : enabledClips.length > 0 ? (
                  <video src={enabledClips[0].blobUrl} className="w-full h-full object-cover" style={{ filter: videoFilterCss }} muted />
                ) : (
                  <div className="text-center">
                    <i className="fas fa-play-circle text-slate-600 text-5xl mb-2" />
                    <p className="text-xs text-slate-500">Video preview</p>
                  </div>
                )}
                {/* Hook overlay */}
                {(hookOptions[selectedHookIdx] || scriptData?.hook) && (
                  <div className="absolute top-0 left-0 right-0 p-4 bg-black/60">
                    <p className="text-white text-base font-black text-center leading-tight drop-shadow-lg">
                      {hookOptions[selectedHookIdx] || scriptData?.hook}
                    </p>
                  </div>
                )}
                {/* CTA overlay */}
                {ctaText && (
                  <div className="absolute bottom-0 left-0 right-0 p-3" style={{ backgroundColor: brandColors.primary + 'CC' }}>
                    <p className="text-white text-xs font-bold text-center">{ctaText}</p>
                  </div>
                )}
                {/* Duration badge */}
                <div className="absolute top-3 right-3 px-2 py-0.5 bg-black/60 rounded-full text-[9px] text-white font-bold">
                  {fmtTime(totalClipDuration)} · {aspectRatio}
                </div>
              </div>

              {caption && <div className="px-4 py-3"><p className="text-xs text-slate-800 whitespace-pre-line">{caption}</p></div>}
              {hashtags.length > 0 && (
                <div className="px-4 pb-3">
                  <p className="text-xs text-blue-600">{hashtags.map(h => `#${h}`).join(' ')}</p>
                </div>
              )}
              <div className="px-4 py-2.5 border-t border-slate-100 flex items-center gap-5 text-[11px] text-slate-400">
                <span><i className="far fa-heart mr-1" />Like</span>
                <span><i className="far fa-comment mr-1" />Comment</span>
                <span><i className="far fa-paper-plane mr-1" />Share</span>
                <span className="ml-auto"><i className="fas fa-bookmark" /></span>
              </div>
            </div>

            {/* Config summary */}
            <div className="border border-slate-200 rounded-2xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                <h3 className="text-xs font-bold text-slate-700">Production Summary</h3>
              </div>
              <div className="p-4 grid grid-cols-2 gap-2.5">
                {[
                  { icon: 'fa-video',            label: 'Format',       value: platform.name },
                  { icon: 'fa-expand',           label: 'Aspect Ratio', value: aspectRatio },
                  { icon: 'fa-palette',          label: 'Colour Grade', value: FILTER_PRESETS.find(f => f.id === effectsConfig.filter)?.label },
                  { icon: 'fa-exchange-alt',     label: 'Transition',   value: TRANSITIONS.find(t => t.id === effectsConfig.transition)?.label },
                  { icon: 'fa-music',            label: 'Music',        value: MUSIC_MOODS.find(m => m.id === audioConfig.mood)?.label },
                  { icon: 'fa-closed-captioning', label: 'Captions',    value: CAPTION_STYLES.find(s => s.id === captionConfig.style)?.label },
                  { icon: 'fa-film',             label: 'Clips',        value: `${enabledClips.length} selected` },
                  { icon: 'fa-clock',            label: 'Total',        value: `${fmtTime(totalClipDuration)}` },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl">
                    <i className={`fas ${item.icon} text-slate-400 text-xs w-4 text-center`} />
                    <div>
                      <p className="text-[9px] text-slate-400 uppercase tracking-wide">{item.label}</p>
                      <p className="text-[10px] font-semibold text-slate-700">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Duration warning */}
            {totalClipDuration > platform.maxDuration && (
              <div className="flex items-start gap-2.5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                <i className="fas fa-exclamation-triangle text-amber-500 text-xs mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-amber-700">Exceeds {platform.name} limit</p>
                  <p className="text-[11px] text-amber-600">Maximum: {platform.maxDuration}s. Please trim clips in the Editor.</p>
                </div>
              </div>
            )}

          </div>
        )}
      </div>

      {/* ═══ BOTTOM BAR ═══ */}
      <div className="flex-shrink-0 border-t border-slate-200 bg-white px-5 py-3">
        <div className="flex items-center justify-between">

          {/* Auto-save pill */}
          <div className="w-32 flex-shrink-0">
            {autoSaveStatus === 'saving' && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full animate-pulse">
                <i className="fas fa-circle-notch fa-spin text-blue-500 text-[9px]" />
                <span className="text-[9px] text-blue-600 font-semibold">Saving…</span>
              </div>
            )}
            {autoSaveStatus === 'saved' && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full">
                <i className="fas fa-check-circle text-emerald-500 text-[9px]" />
                <span className="text-[9px] text-emerald-600 font-semibold">Auto-saved ✓</span>
              </div>
            )}
            {autoSaveStatus === 'idle' && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full">
                <i className="fas fa-cloud text-slate-300 text-[9px]" />
                <span className="text-[9px] text-slate-300">Auto-save on</span>
              </div>
            )}
          </div>

          {/* Section indicator */}
          <div className="flex items-center gap-1">
            {SECTION_IDS.map((sid, i) => (
              <div key={sid} className={`h-1.5 rounded-full transition-all ${i === sectionIdx ? 'w-5 bg-slate-800' : 'w-1.5 bg-slate-200'}`} />
            ))}
          </div>

          {/* Back / Next */}
          <div className="flex items-center gap-2">
            {sectionIdx > 0 && (
              <button onClick={() => setActiveSection(SECTION_IDS[sectionIdx - 1])}
                className="px-4 py-2 text-[11px] font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all flex items-center gap-1.5">
                <i className="fas fa-arrow-left text-[9px]" />Back
              </button>
            )}
            {sectionIdx < SECTION_IDS.length - 1 ? (
              <button onClick={() => {
                const nextSection = SECTION_IDS[sectionIdx + 1];
                if (nextSection === 'edit') { editTriggeredRef.current = false; }
                setActiveSection(nextSection);
              }}
                className="px-5 py-2 text-[11px] font-semibold text-white bg-slate-900 rounded-xl hover:bg-slate-800 transition-all flex items-center gap-1.5">
                {{today: 'Write Script', script: 'Start Filming', film: 'Edit with AI'}[activeSection] || 'Next'}
                <i className="fas fa-arrow-right text-[9px]" />
              </button>
            ) : (
              <button onClick={() => { setShowPublishConfirm(true); setPublishError(""); }}
                className="px-5 py-2 text-[11px] font-bold text-white bg-blue-600 rounded-xl transition-all flex items-center gap-1.5 shadow-sm hover:bg-blue-700">
                <i className="fas fa-paper-plane text-[10px]" />Publish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* ══ PUBLISH CONFIRMATION MODAL ══ */}
    {showPublishConfirm && (() => {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const isLocalhost = API_URL.includes('localhost') || API_URL.includes('127.0.0.1');
      const platformKey = dayRef.current?.platform || 'instagram';
      const hasBlocking = isLocalhost;

      return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-paper-plane text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Publish Video</h3>
                <p className="text-[11px] text-slate-500">
                  Post to {(PLATFORM_CFG[platformKey] || PLATFORM_CFG.instagram).name}
                </p>
              </div>
            </div>

            {!exportedBlobRef.current && (
              <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-[11px] text-amber-800">
                <i className="fas fa-exclamation-triangle mt-0.5 flex-shrink-0" />
                <span><strong>Finalise first.</strong> Click "Finalise Video" to render your video with overlays, then publish.</span>
              </div>
            )}

            {exportedBlobRef.current && isLocalhost && (
              <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-[11px] text-amber-800">
                <i className="fas fa-ban mt-0.5 flex-shrink-0" />
                <span><strong>Only works on live app.</strong> Instagram &amp; Facebook cannot reach localhost. Open digitaltrent.co.uk to publish directly.</span>
              </div>
            )}

            {exportedBlobRef.current && !hasBlocking && (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl text-[11px] text-green-700">
                <i className="fas fa-check-circle flex-shrink-0" />
                Video ready ({(exportedBlobRef.current.size / 1024 / 1024).toFixed(1)} MB) — uploading &amp; publishing automatically
              </div>
            )}

            {publishError && (
              <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-[11px] text-red-700">
                <i className="fas fa-times-circle mt-0.5 flex-shrink-0" /><span>{publishError}</span>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button onClick={() => { setShowPublishConfirm(false); setPublishError(''); }}
                disabled={isPublishing}
                className="flex-1 py-2.5 text-[11px] font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handlePublishConfirm}
                disabled={isPublishing || !exportedBlobRef.current || hasBlocking}
                className="flex-1 py-2.5 text-[11px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-1.5">
                {isPublishing ? (
                  <><i className="fas fa-circle-notch fa-spin text-[10px]" />Publishing…</>
                ) : (
                  <><i className="fas fa-paper-plane text-[10px]" />Publish</>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      );
    })()}

    {/* ── PHONE PREVIEW POPUP ────────────────────────────────────────────── */}
    {showPreview && previewBlobUrl && ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
        onClick={e => { if (e.target === e.currentTarget) { setShowPreview(false); setPreviewPlaying(false); stopMusic(); } }}>

        {/* Phone mockup */}
        <div className="relative flex flex-col rounded-[38px] shadow-2xl overflow-hidden mb-4 sm:mb-0"
          style={{ width: 300, height: 612, background: '#111', border: '8px solid #1a1a1a', boxShadow: '0 0 0 2px #333, 0 30px 80px rgba(0,0,0,0.8)' }}>

          {/* Phone notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 rounded-b-2xl z-30" style={{ background: '#1a1a1a' }} />

          {/* Video */}
          <video
            ref={previewVideoRef}
            src={previewBlobUrl}
            onLoadedMetadata={() => {
              const vid = previewVideoRef.current;
              if (!vid) return;
              vid.currentTime = currentTime;
              const c = previewCanvasRef.current;
              if (c) { c.width = vid.videoWidth || 1080; c.height = vid.videoHeight || 1920; }
            }}
            onEnded={() => { setPreviewPlaying(false); stopMusic(); }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', filter: videoFilterCss }}
            playsInline preload="auto"
            className="block"
          />

          {/* Canvas overlay for text overlays */}
          <canvas ref={previewCanvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 10 }} />

          {/* Play / pause tap area */}
          <div className="absolute inset-0 z-20 flex items-center justify-center cursor-pointer"
            onClick={() => {
              const vid = previewVideoRef.current;
              if (!vid) return;
              if (previewPlaying) {
                if (!vid.paused) vid.pause();
                setPreviewPlaying(false);
                stopMusic();
              } else {
                // startMusic synchronously — must be in user-gesture handler
                startMusic();
                vid.play()
                  .then(() => setPreviewPlaying(true))
                  .catch(() => {
                    vid.muted = true;
                    vid.play()
                      .then(() => setPreviewPlaying(true))
                      .catch(() => { setPreviewPlaying(false); stopMusic(); });
                  });
              }
            }}>
            {!previewPlaying && (
              <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-2xl" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '2px solid rgba(255,255,255,0.3)' }}>
                <i className="fas fa-play text-white text-lg ml-1" />
              </div>
            )}
          </div>

          {/* Bottom bar: scrubber + time */}
          <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-4 pt-8" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)' }}>
            <div className="w-full h-1 rounded-full cursor-pointer mb-2 relative"
              style={{ background: 'rgba(255,255,255,0.25)' }}
              onClick={e => {
                const r = e.currentTarget.getBoundingClientRect();
                const pct = (e.clientX - r.left) / r.width;
                const t = pct * (totalDuration || totalClipDuration || 1);
                if (previewVideoRef.current) previewVideoRef.current.currentTime = t;
                setPreviewCurTime(t);
              }}>
              <div className="h-full rounded-full" style={{ width: `${(totalDuration || totalClipDuration) > 0 ? (previewCurTime / (totalDuration || totalClipDuration)) * 100 : 0}%`, background: 'rgba(255,255,255,0.9)' }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/70 text-[9px] font-mono">{fmtTime(previewCurTime)} / {fmtTime(totalDuration || totalClipDuration)}</span>
              <span className="text-white/50 text-[9px] font-medium capitalize">{audioConfig.mood !== 'none' ? audioConfig.mood : 'no music'}</span>
            </div>
          </div>
        </div>

        {/* Close + info */}
        <div className="absolute top-4 right-4 flex flex-col items-end gap-2">
          <button onClick={() => { setShowPreview(false); setPreviewPlaying(false); stopMusic(); }}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
            <i className="fas fa-times text-sm" />
          </button>
        </div>
        <div className="absolute top-4 left-4">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10">
            <i className="fas fa-mobile-alt text-white/70 text-[10px]" />
            <span className="text-white/70 text-[10px] font-medium">Preview · {aspectRatio}</span>
          </div>
          {textOverlays.length > 0 && (
            <div className="mt-1.5 px-3 py-1 rounded-full bg-violet-500/20 border border-violet-400/30">
              <span className="text-violet-300 text-[9px] font-medium"><i className="fas fa-font mr-1 text-[8px]" />{textOverlays.length} text overlay{textOverlays.length !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>

      </div>,
      document.body
    )}

    {publishSuccess && ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <i className="fas fa-check-circle text-green-500 text-3xl" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900 mb-1">Published!</h3>
            <p className="text-[12px] text-slate-500">
              Your video is live on {(PLATFORM_CFG[dayRef.current?.platform] || PLATFORM_CFG.instagram).name}.
              The post has been marked as complete in your calendar.
            </p>
          </div>
          <button onClick={() => { setPublishSuccess(false); onClose?.(); }}
            className="w-full py-3 text-[12px] font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition-all">
            Done
          </button>
        </div>
      </div>,
      document.body
    )}
    </>
  );
}
